import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
    CalendarCheck, ChevronLeft, ChevronRight, Plus, Clock, Car, MoreVertical,
    User, X, Loader2, AlertCircle, CheckCircle, AlertTriangle, Phone, Mail,
    GripVertical, Edit2, Trash2, Eye, Search, Shield, Gauge, FileImage,
    CreditCard, LayoutDashboard, Paperclip, ArrowUpRight, ArrowDownLeft, Info,
    CalendarClock, Hash, Building2, Wrench
} from 'lucide-react';
import Layout from './Layout';
import {
    fetchBookings,
    fetchBookingsByDate,
    fetchBookingsForWeek,
    fetchBookingsForMonth,
    fetchBookingSummary,
    fetchBookingServiceCategories,
    fetchLoanCarImageCategories,
    fetchLoanCarIncidents,
    createBooking,
    createServiceProfileBooking,
    createMileageBooking,
    updateBooking,
    updateBookingPersons,
    cancelBooking,
    rescheduleBooking,
    updateBookingStatus,
    markVehicleReceived,
    setViewMode,
    navigateToToday,
    navigateNext,
    navigatePrevious,
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
    openViewModal,
    closeViewModal,
    openCancelConfirm,
    closeCancelConfirm,
    openVehicleReceivedModal,
    closeVehicleReceivedModal,
    setDraggingBooking,
    clearDragState,
    clearOperationStatus,
} from '../store/slices/bookingSlice';
import {
    fetchJob,
    fetchJobResources,
    fetchAvailableResources,
    fetchWorkCategories,
    fetchCategoryTypes,
    startJob,
    pauseJob,
    resumeJob,
    assignStaff,
    addWorkItem,
    updateWorkItem,
    removeWorkItem,
    addJobNote,
    assignResource,
    unassignResource,
    clearOperationStatus as clearJobOperationStatus,
} from '../store/slices/jobSlice';
import axiosInstance from '../services/axiosInstance';
import { selectAllocationsByBooking } from '../store/slices/loanCarAllocationSlice';
import LoanCarAllocationPanel from './LoanCarAllocationPanel';
import BookingFormModal from './BookingFormModal';
import VehicleReceivedModal from './VehicleReceivedModal';
import JobDetailModal from './JobDetailModal';
import { useOpenInvoice } from '../hooks/useRecordNavigation';
import PhoneBookingsDrawer from './PhoneBookingsDrawer';
import BotBookingsPanel from './BotBookingsPanel';
import { fetchPreBookings, convertPreBooking } from '../store/slices/phoneBookingSlice';
import { TransmissionBadge } from './TransmissionIcons';
import useEscapeToClose from './useEscapeToClose';
import PickupConfirmModal from './PickupConfirmModal';

// ==================== Constants ====================

const BOOKING_STATUSES = [
    'SCHEDULED',
    'VEHICLE_RECEIVED',
    'COMPLETED',
    'NO_SHOW'
];

const TIME_SLOTS = [
    '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
    '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'
];

const REGO_STATES = ['VIC', 'NSW', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

// Height (in px) of a single 1-hour slot in Day/Week grids.
// Day/Week rows are laid out with a fixed height so booking blocks can
// span multiple slots proportionally to their estimatedDurationMinutes.
const SLOT_HEIGHT_PX = 60;
const SLOT_MINUTES = 60;

// Every booking block renders at least one full hour tall, whatever its real
// duration. A 30-minute booking drawn to scale is a ~28px sliver that fits only
// a truncated customer name — unreadable at a glance, which is the whole point
// of the diary. Blocks still grow past this for longer bookings; short ones are
// simply floored. The trade is that a sub-hour booking overstates its slot
// visually, so the tooltip carries the real duration.
const MIN_BLOCK_HEIGHT_PX = SLOT_HEIGHT_PX - 4;

// A booking whose duration meets or exceeds this threshold (minutes) is
// treated as an "all day" booking in the month view — renders as a filled
// day tile instead of a stacked pill.
const FULL_DAY_MINUTES = 480; // 8 hours

// Rotating palette used to visually distinguish multiple bookings on the
// same day. Colour is assigned per-booking (by index within that day) so
// staff can tell at a glance which block belongs to which booking when
// two or more overlap in the same time window.
//
// Excludes blue, emerald/teal, amber, and gray — those hues are already
// used by the header summary badges (Scheduled, In Progress, Completed,
// No Show) and reusing them on booking blocks would create false visual
// associations with booking status.
const BOOKING_PALETTE = [
    { bg: 'bg-violet-100',  border: 'border-violet-300',  text: 'text-violet-900',  bar: 'bg-violet-500',  ring: 'ring-violet-400' },
    { bg: 'bg-rose-100',    border: 'border-rose-300',    text: 'text-rose-900',    bar: 'bg-rose-500',    ring: 'ring-rose-400' },
    { bg: 'bg-fuchsia-100', border: 'border-fuchsia-300', text: 'text-fuchsia-900', bar: 'bg-fuchsia-500', ring: 'ring-fuchsia-400' },
    { bg: 'bg-lime-100',    border: 'border-lime-300',    text: 'text-lime-900',    bar: 'bg-lime-500',    ring: 'ring-lime-400' },
    { bg: 'bg-orange-100',  border: 'border-orange-300',  text: 'text-orange-900',  bar: 'bg-orange-500',  ring: 'ring-orange-400' },
    { bg: 'bg-indigo-100',  border: 'border-indigo-300',  text: 'text-indigo-900',  bar: 'bg-indigo-500',  ring: 'ring-indigo-400' },
    { bg: 'bg-pink-100',    border: 'border-pink-300',    text: 'text-pink-900',    bar: 'bg-pink-500',    ring: 'ring-pink-400' },
    { bg: 'bg-red-100',     border: 'border-red-300',     text: 'text-red-900',     bar: 'bg-red-500',     ring: 'ring-red-400' },
];

// ==================== Helper Functions ====================

const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

const formatShortDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
};

const formatTime = (time) => {
    if (!time) return '';
    return time.substring(0, 5);
};

const formatDateTime = (dt) => {
    if (!dt) return '';
    return new Date(dt).toLocaleString('en-AU', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const getWeekDates = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    const dates = [];
    for (let i = 0; i < 7; i++) {
        const date = new Date(monday);
        date.setDate(monday.getDate() + i);
        dates.push(date);
    }
    return dates;
};

const getMonthDates = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = d.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const startDate = new Date(firstDay);
    const dayOfWeek = startDate.getDay();
    startDate.setDate(startDate.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

    const dates = [];
    const current = new Date(startDate);
    while (current <= lastDay || dates.length % 7 !== 0) {
        dates.push(new Date(current));
        current.setDate(current.getDate() + 1);
    }
    return dates;
};

const toISODateString = (date) => {
    if (!date) return null;
    if (typeof date === 'string') return date.split('T')[0];
    return date.toISOString().split('T')[0];
};

const getStatusColor = (status) => {
    const colors = {
        SCHEDULED: 'bg-blue-50 border-blue-200 text-blue-700',
        VEHICLE_RECEIVED: 'bg-emerald-50 border-emerald-200 text-emerald-700',
        COMPLETED: 'bg-gray-50 border-gray-200 text-gray-600',
        CANCELLED: 'bg-red-50 border-red-200 text-red-700',
        NO_SHOW: 'bg-amber-50 border-amber-200 text-amber-700',
    };
    return colors[status] || colors.SCHEDULED;
};

const getStatusBadgeColor = (status) => {
    const colors = {
        SCHEDULED: 'bg-blue-100 text-blue-700',
        VEHICLE_RECEIVED: 'bg-emerald-100 text-emerald-700',
        COMPLETED: 'bg-gray-100 text-gray-600',
        CANCELLED: 'bg-red-100 text-red-700',
        NO_SHOW: 'bg-amber-100 text-amber-700',
    };
    return colors[status] || colors.SCHEDULED;
};

// ── Duration / layout helpers ───────────────────────────────────────────────

/**
 * Convert "HH:MM[:SS]" to an offset in minutes from the first calendar slot
 * (TIME_SLOTS[0]). Returns null if the time is outside the visible range.
 */
const timeToSlotOffsetMinutes = (time) => {
    if (!time) return null;
    const [h, m] = time.substring(0, 5).split(':').map(Number);
    const [baseH, baseM] = TIME_SLOTS[0].split(':').map(Number);
    const bookingMinutes = h * 60 + m;
    const baseMinutes = baseH * 60 + baseM;
    return bookingMinutes - baseMinutes;
};

/**
 * Duration the booking should visually occupy, in minutes.
 *
 * Priority:
 *   1. backend-computed estimatedDurationMinutes (the canonical value)
 *   2. sum of serviceTypes[].durationMinutes (frontend fallback — covers
 *      cases where the backend hasn't populated the top-level field yet)
 *   3. a single slot (60 min) so old/ad-hoc bookings don't vanish
 */
const getBookingDurationMinutes = (booking) => {
    const raw = Number(booking?.estimatedDurationMinutes);
    if (Number.isFinite(raw) && raw > 0) return raw;

    // Fallback: sum durations from the serviceTypes array
    if (Array.isArray(booking?.serviceTypes) && booking.serviceTypes.length > 0) {
        const sum = booking.serviceTypes.reduce((acc, st) => {
            const d = Number(st.durationMinutes);
            return acc + (Number.isFinite(d) && d > 0 ? d : 0);
        }, 0);
        if (sum > 0) return sum;
    }

    return SLOT_MINUTES;
};

/**
 * Normalises a booking's service-types into a consistent array shape for rendering.
 *
 * Handles three input shapes:
 *   1. DTO:        booking.serviceTypes = [{ serviceName, durationMinutes, description }]
 *   2. Back-compat: booking.primaryServiceType = "BRAKES" (set by BookingResponse)
 *   3. Missing:    no service types at all (e.g. booking made before the flow was used)
 *
 * The backend DTO (ServiceTypeEntryDto) sends { serviceName, durationMinutes, description }.
 * This helper normalises to { type, durationMinutes, description } so all downstream
 * rendering code can read entry.type consistently.
 *
 * For profile-backed bookings the backend synthesises a single entry whose
 * serviceName is the profile name — these entries flow through unchanged. The caller
 * can check booking.serviceProfileName to distinguish them visually.
 *
 * Always returns an array (possibly empty) of { type, durationMinutes, description }.
 */
const getBookingServiceTypes = (booking) => {
    if (Array.isArray(booking?.serviceTypes) && booking.serviceTypes.length > 0) {
        return booking.serviceTypes.map(e => ({
            type: e.serviceName ?? e.type,
            durationMinutes: e.durationMinutes,
            description: e.description,
        }));
    }
    if (booking?.primaryServiceType) {
        return [{ type: booking.primaryServiceType, durationMinutes: null, description: null }];
    }
    return [];
};

/** Format a CategoryType enum name as a friendly label. */
const formatServiceTypeLabel = (type) =>
    typeof type === 'string' ? type.replace(/_/g, ' ') : '';

/** Format a per-row duration in minutes as "1h 30m" / "45m". */
const formatDurationShort = (mins) => {
    const n = Number(mins);
    if (!Number.isFinite(n) || n <= 0) return null;
    const h = Math.floor(n / 60);
    const m = n % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
};

/**
 * Stable per-booking colour scheme, picked from BOOKING_PALETTE based on the
 * booking's position within the day's bookings (sorted by start time). This
 * guarantees the first booking of every day starts on index 0 so the palette
 * feels intentional rather than random.
 */
const getSchemeByIndex = (idx) => BOOKING_PALETTE[idx % BOOKING_PALETTE.length];

/**
 * Sort bookings by start time ascending; bookings without a time sink to the
 * end. Used to make palette assignment and vertical layout deterministic.
 */
const sortBookingsByTime = (list) =>
    [...list].sort((a, b) => {
        const ta = a.scheduledTime || '99:99';
        const tb = b.scheduledTime || '99:99';
        return ta.localeCompare(tb);
    });

// ==================== Main Component ====================

const BookingsPage = () => {
    const dispatch = useDispatch();
    // Following a job's invoice link, without a page reload.
    const openInvoice = useOpenInvoice();
    // Plain store reference for read-at-decision-time access to the
    // loanCarAllocations slice from inside handlePickupConfirm. We don't
    // useSelector this because we only need the value at the instant the
    // pickup popup is confirmed — subscribing would re-render BookingsPage
    // every time the allocations slice changes elsewhere.
    const store = useStore();
    const {
        bookings,
        bookingsLoading,
        bookingsError,
        viewMode,
        currentDate,
        summary,
        showCreateModal,
        showEditModal,
        showViewModal,
        showCancelConfirm,
        showVehicleReceivedModal,
        selectedBooking,
        modalContext,
        draggingBooking,
        operationLoading,
        operationError,
        operationSuccess,
        bookingServiceCategories,
        metaLoading,
        loanCarImageCategories,
        loanCarIncidents,
        loanCarIncidentsLoading,
    } = useSelector(state => state.bookings);

    /*
     * How many captured calls are still waiting to be turned into bookings. Drives the
     * badge on the "Calls to book" button.
     */
    const { totalItems: phoneCallCount } = useSelector(state => state.preBookings);

    /*
     * ── Inline JobDetailModal support ─────────────────────────────────────
     * Reads the subset of the jobs slice needed to render JobDetailModal from
     * inside BookingsPage so staff can navigate booking → job → booking
     * without leaving the calendar context. Only populated while
     * viewingJobUuid is non-null; the modal handles its own lifecycle and
     * any required follow-up dispatches via the wired handlers below.
     */
    const {
        selectedJob,
        operationLoading: jobOperationLoading,
        detailLoading: jobDetailLoading,
        operationError: jobOperationError,
        jobResources,
        availableResources,
        resourcesLoading,
        workCategories,
        categoryTypes: jobCategoryTypes,
    } = useSelector(state => state.jobs);

    // Fetch calendar data whenever view or date changes
    useEffect(() => {
        const date = new Date(currentDate);
        if (viewMode === 'day') {
            dispatch(fetchBookingsByDate(toISODateString(date)));
        } else if (viewMode === 'week') {
            const weekStart = getWeekDates(date)[0];
            dispatch(fetchBookingsForWeek(toISODateString(weekStart)));
        } else {
            dispatch(fetchBookingsForMonth({
                year: date.getFullYear(),
                month: date.getMonth() + 1
            }));
        }
        dispatch(fetchBookingSummary());
    }, [viewMode, currentDate, dispatch]);

    /*
     * The pre-booking queue: calls the AI line took that nobody has turned into a booking
     * yet. Deliberately NOT scoped to the calendar's date — an unbooked call from Friday is
     * still owed a phone call on Monday, and date-scoping would hide it.
     *
     * Fetched on mount so the button badge shows a live count before the panel is opened.
     */
    useEffect(() => {
        dispatch(fetchPreBookings({ page: 0 }));
    }, [dispatch]);

    // Fetch category types once — only if not already loaded
    useEffect(() => {
        if (bookingServiceCategories.length === 0) {
            dispatch(fetchBookingServiceCategories());
        }
        if (loanCarImageCategories.length === 0) {
            dispatch(fetchLoanCarImageCategories());
        }
        // Job CategoryTypes — needed by JobDetailModal's Work Item picker when
        // a job is opened inline from a booking. Cheap one-shot; the jobs
        // slice caches the result.
        if (jobCategoryTypes.length === 0) {
            dispatch(fetchCategoryTypes());
        }
    }, [dispatch, bookingServiceCategories.length, loanCarImageCategories.length, jobCategoryTypes.length]);

    useEffect(() => {
        if (operationSuccess) {
            const timer = setTimeout(() => dispatch(clearOperationStatus()), 3000);
            return () => clearTimeout(timer);
        }
    }, [operationSuccess, dispatch]);

    const getBookingsForDate = useCallback((date) => {
        const dateStr = toISODateString(date);
        return bookings.filter(b => b.scheduledDate === dateStr);
    }, [bookings]);

    const getBookingsForSlot = useCallback((date, time) => {
        const dateStr = toISODateString(date);
        return bookings.filter(b =>
            b.scheduledDate === dateStr &&
            formatTime(b.scheduledTime) === time
        );
    }, [bookings]);

    const handleCellClick = (date, time) => {
        dispatch(openCreateModal({ date: toISODateString(date), time }));
    };

    /*
     * Turn a captured call into a real booking.
     *
     * Opens the NORMAL create-booking form seeded with what the caller said, so the
     * Customer, Vehicle and Booking are all created through the same validated path as a
     * staff booking — nothing the bot heard is written to those tables unread.
     *
     * The pre-booking is marked converted only once the booking actually exists (see the
     * create-modal onSuccess), otherwise abandoning the form would silently clear the queue.
     */
    const handleBookPreBooking = (pre) => {
        setPendingPreBooking(pre);
        dispatch(openCreateModal({
            date: pre.requestedDate || undefined,
            customerName: pre.customerName || undefined,
            customerPhone: pre.callerNumber || undefined,
            vehicleRego: pre.vehicleRego || undefined,
        }));
    };

    /*
     * Clears the queue row once the booking genuinely exists.
     *
     * Only on a FULFILLED create: a validation failure leaves the form open and the customer
     * still un-booked, so the row must stay. Failing to record the link is logged rather
     * than surfaced — the booking succeeded, and blocking on the bookkeeping would be worse
     * than a duplicate row someone can dismiss.
     */
    const settlePreBooking = async (result) => {
        if (!pendingPreBooking) return;
        if (result?.meta?.requestStatus !== 'fulfilled') return;
        await dispatch(convertPreBooking({
            uuid: pendingPreBooking.uuid,
            bookingUuid: result.payload?.uuid ?? null,
        }));
        setPendingPreBooking(null);
    };

    const handleBookingClick = (booking) => {
        dispatch(openViewModal(booking));
        if (booking.hasLoanCar) {
            dispatch(fetchLoanCarIncidents(booking.uuid));
        }
    };

    const handleDragStart = (booking) => {
        dispatch(setDraggingBooking(booking));
    };

    // Track drop target locally for visual feedback
    const [dropTarget, setDropTarget] = useState(null);

    /**
     * Local state for the new pre-vehicle-received pickup confirmation popup.
     * When non-null, the popup is open for that booking; setting back to null
     * closes it. Kept local (not in slice) because it's a transient pre-step
     * that always resolves to either dispatching markVehicleReceived or
     * opening the existing VehicleReceivedModal — no other component needs
     * to read it.
     */
    const [pickupConfirmFor, setPickupConfirmFor] = useState(null);

    /*
     * Tracks which job UUID is being viewed in the inline JobDetailModal
     * (opened from "View Job" inside BookingDetailModal). Local because the
     * underlying booking detail modal is also still rendered — closing the
     * job modal returns the user to the booking, so this is a transient
     * navigation state that no other component needs to read.
     *
     * Tab is preserved across the back-trip — JobDetailModal uses initialTab,
     * but we always open on the default 'details' tab from the booking.
     */
    const [viewingJobUuid, setViewingJobUuid] = useState(null);

    /*
     * Phone booking activity drawer. Deliberately local state and not in the redux
     * bookings slice: it shows call ATTEMPTS, most of which never became a booking, so it
     * shares no data with the calendar and must not be invalidated by booking refetches.
     */
    const [phoneDrawerOpen, setPhoneDrawerOpen] = useState(false);

    /*
     * Side panel listing the bot's bookings for whatever range the calendar shows.
     * Derived from the same `bookings` array the grid renders, so the two can never
     * disagree — no separate fetch, no separate loading state.
     */
    const [botPanelOpen, setBotPanelOpen] = useState(false);

    /*
     * The pre-booking currently being converted, if any. Held while the create-booking form
     * is open so the queue row can be cleared once — and only once — a real booking exists.
     */
    const [pendingPreBooking, setPendingPreBooking] = useState(null);

    /*
     * Tracks which job UUID is being assigned to a staff member via the
     * inline AssignStaffPicker (opened from JobDetailModal's "Assign" button
     * when JobDetailModal was launched from the bookings page). Stacked
     * above viewingJobUuid: setting this opens the picker, clearing it
     * dismisses the picker and returns the user to JobDetailModal.
     *
     * Kept local because JobsPage has its own AssignStaffModal — this
     * picker exists only for the booking-launched path so staff don't have
     * to leave the calendar to assign a job.
     */
    const [assigningJobUuid, setAssigningJobUuid] = useState(null);

    /**
     * Opens the inline JobDetailModal for a booking's auto-created job.
     * Prefetches everything JobDetailModal expects in state: the job itself,
     * its resources, the available pool, and the work-category enum (used by
     * the AddWorkItem modal inside JobDetailModal).
     *
     * No-op when the booking has no jobUuid — the View Job button is hidden
     * in that case, but this guard keeps the handler safe if called directly.
     */
    const handleViewJob = useCallback((jobUuid) => {
        if (!jobUuid) return;
        // Clear any stale error from a previous job interaction so the
        // banner inside JobDetailModal starts clean.
        dispatch(clearJobOperationStatus());
        dispatch(fetchJob(jobUuid));
        dispatch(fetchJobResources(jobUuid));
        dispatch(fetchAvailableResources({ jobUuid }));
        // workCategories is cached after the first fetch; safe to redispatch.
        if (!workCategories || workCategories.length === 0) {
            dispatch(fetchWorkCategories());
        }
        setViewingJobUuid(jobUuid);
    }, [dispatch, workCategories]);

    const handleDragOver = (e, date, time) => {
        e.preventDefault();
        if (date) {
            setDropTarget({ date: toISODateString(date), time });
        } else {
            setDropTarget(null);
        }
    };

    const handleDrop = (date, time) => {
        if (draggingBooking) {
            dispatch(rescheduleBooking({
                uuid: draggingBooking.uuid,
                newDate: toISODateString(date),
                newTime: time
            }));
        }
        setDropTarget(null);
        dispatch(clearDragState());
    };

    const handleDragEnd = () => {
        setDropTarget(null);
        dispatch(clearDragState());
    };

    /**
     * Called when a status button is clicked in BookingDetailModal.
     *
     * For VEHICLE_RECEIVED: open the pickup confirmation popup first. The
     * popup lets staff verify / edit the recorded pickup person, then proceeds
     * to either the loan-car handover modal (if a loan car is allocated) or
     * directly fires the vehicle-received transition (no loan car).
     *
     * For all other statuses: dispatch the generic updateBookingStatus thunk.
     */
    const handleStatusChange = (booking, status) => {
        if (status === 'VEHICLE_RECEIVED') {
            setPickupConfirmFor(booking);
            return;
        }
        dispatch(updateBookingStatus({ uuid: booking.uuid, status }));
    };

    /**
     * Build a complete UpdateBooking PUT body from a BookingResponse plus
     * optional overrides. The PUT endpoint requires the full booking shape
     * (scheduledDate, customer.uuid, vehicle.uuid, etc), so we reconstruct
     * everything from current state and let the caller mutate just the
     * fields they want to change.
     *
     * Service-type handling:
     *   - Ad-hoc bookings: serviceTypes carries CategoryType enum names in
     *     `serviceName` — passed through unchanged as `type` for the update DTO.
     *   - Service-profile bookings: the response synthesises a single fake
     *     entry with the profile name as `serviceName` (a string, not an
     *     enum). Sending that as `type` would fail backend validation, so
     *     we send an empty array — matching what the backend stores for
     *     profile-backed bookings.
     */
    const buildUpdateBodyFromBooking = (booking, overrides = {}) => {
        const isProfileBooking = !!booking.serviceProfileName;
        const serviceTypesForUpdate = isProfileBooking
            ? []
            : (booking.serviceTypes ?? []).map(e => ({
                type:            e.serviceName ?? e.type,
                durationMinutes: e.durationMinutes ?? null,
                description:     e.description ?? null,
            }));

        return {
            scheduledDate:            booking.scheduledDate,
            scheduledTime:            booking.scheduledTime ?? null,
            serviceTypes:             serviceTypesForUpdate,
            description:              booking.description ?? null,
            estimatedDurationMinutes: booking.estimatedDurationMinutes ?? null,
            customer:                 { uuid: booking.customerUuid },
            vehicle:                  { uuid: booking.vehicleUuid },
            handoverPersonName:       booking.handoverPersonName   ?? null,
            handoverPersonMobile:     booking.handoverPersonMobile ?? null,
            pickupPersonName:         booking.pickupPersonName     ?? null,
            pickupPersonMobile:       booking.pickupPersonMobile   ?? null,
            ...overrides,
        };
    };

    /**
     * Called when PickupConfirmModal confirms.
     *
     * Sequence:
     *   1. If pickup person changed → PUT /bookings/{uuid} with the full body
     *      (only the two pickup fields are mutated). We await this so the
     *      vehicle-received transition sees the updated booking.
     *   2. Close the popup.
     *   3. Branch on loan-car presence:
     *        - has loan car → open existing VehicleReceivedModal (which
     *          collects the handover details and fires markVehicleReceived
     *          itself)
     *        - no loan car → fire markVehicleReceived directly with
     *          loanCarDetails: null
     *
     * If the PUT fails the operationError flow already surfaces it via the
     * page-level banner, and we don't proceed to the vehicle-received step
     * (the popup stays closed but the user can re-open via the status button).
     */
    const handlePickupConfirm = async ({ pickupPersonName, pickupPersonMobile, customerEmail, customerName, changed }) => {
        const booking = pickupConfirmFor;
        if (!booking) return;

        if (changed) {
            const body = buildUpdateBodyFromBooking(booking, {
                pickupPersonName,
                pickupPersonMobile,
            });
            const result = await dispatch(updateBooking({ uuid: booking.uuid, data: body }));
            if (result.meta.requestStatus !== 'fulfilled') {
                // Page-level error banner already shows operationError.
                // Leave the popup open so the user sees what happened and
                // can either retry or cancel.
                return;
            }
        }

        setPickupConfirmFor(null);

        // ── Decide whether to open the loan-car handover modal ──
        //
        // We can't trust `booking.hasLoanCar` alone here. There are two
        // distinct failure modes the field misses:
        //
        //   1. Calendar list endpoints (/bookings/week, /bookings/month,
        //      /bookings/date) return the lean BookingSummaryResponse —
        //      it does include `hasLoanCar`, so this case works.
        //
        //   2. Staff allocate a loan car in the SAME session, after the
        //      BookingDetailModal is already open. The new allocation
        //      lands in the loanCarAllocations slice, but the booking
        //      object in the bookings slice is NEVER refreshed — its
        //      `hasLoanCar` is stuck at false. Branching on the booking
        //      field alone in this case sends {loanCarDetails: null} to
        //      the backend and triggers a 400 "this booking has an active
        //      loan car allocation" — exactly what the user reported.
        //
        // The loanCarAllocations slice is the authoritative live source
        // (LoanCarAllocationPanel keeps it in sync via fetchBookingAllocations
        // and the allocate/fulfilled reducer). Read it once, at the moment
        // of decision, via store.getState() so we don't subscribe and
        // re-render BookingsPage on every allocation change elsewhere.
        const liveAllocations = selectAllocationsByBooking(store.getState(), booking.uuid) || [];
        const hasLoanCar = booking.hasLoanCar || liveAllocations.length > 0;

        if (hasLoanCar) {
            // Carry the email onto the booking handed to the loan-car modal, so the
            // transition it eventually fires still has it. Without this the loan-car path
            // would hit the backend's email guard and dead-end — the exact failure this
            // whole field exists to prevent, just one branch further along.
            dispatch(openVehicleReceivedModal(
                (customerEmail || customerName)
                    ? { ...booking, pendingCustomerEmail: customerEmail, pendingCustomerName: customerName }
                    : booking
            ));
        } else {
            dispatch(markVehicleReceived({
                uuid: booking.uuid,
                loanCarDetails: null,
                customerEmail,
                customerName,
            }));
        }
    };

    /**
     * Called when VehicleReceivedModal confirms.
     * loanCarDetails is { notes, images } when the user filled in the form,
     * or null when the booking has no loan car (modal bypasses the section).
     */
    const handleVehicleReceivedConfirm = ({ loanCarDetails }) => {
        dispatch(markVehicleReceived({
            uuid: selectedBooking.uuid,
            loanCarDetails,
            // Stashed by the pickup-confirm step for a customer with no email (phone-bot
            // bookings). Both handover paths converge on the same backend guard, so it
            // has to travel this branch too.
            customerEmail: selectedBooking?.pendingCustomerEmail ?? null,
            customerName: selectedBooking?.pendingCustomerName ?? null,
        }));
    };

    return (
        <Layout>
            <div className="p-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                            <CalendarCheck size={20} className="text-emerald-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Bookings</h1>
                            <p className="text-sm text-gray-500">Manage workshop appointments</p>
                        </div>
                        {/* Summary Badges */}
                        <div className="hidden md:flex items-center gap-2 ml-4 pl-4 border-l border-gray-200">
                            <SummaryBadge label="Scheduled" value={summary?.scheduled || 0} color="blue" />
                            <SummaryBadge label="In Progress" value={summary?.vehicleReceived || 0} color="emerald" />
                            <SummaryBadge label="Completed" value={summary?.completed || 0} color="gray" />
                            <SummaryBadge label="No Show" value={summary?.noShow || 0} color="amber" />
                        </div>
                    </div>
                    <button
                        onClick={() => dispatch(openCreateModal())}
                        className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-medium"
                    >
                        <Plus size={18} />
                        New Booking
                    </button>
                </div>

                {/* Mobile Summary Row */}
                <div className="flex md:hidden items-center gap-2 mb-4 overflow-x-auto pb-2">
                    <SummaryBadge label="Scheduled" value={summary?.scheduled || 0} color="blue" />
                    <SummaryBadge label="In Progress" value={summary?.vehicleReceived || 0} color="emerald" />
                    <SummaryBadge label="Completed" value={summary?.completed || 0} color="gray" />
                    <SummaryBadge label="No Show" value={summary?.noShow || 0} color="amber" />
                </div>

                {/* Success/Error Messages */}
                {operationSuccess && (
                    <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2 text-emerald-700">
                        <CheckCircle size={18} />
                        {operationSuccess}
                    </div>
                )}
                {(operationError || bookingsError) && !showVehicleReceivedModal && (
                    // Suppress the page-level error banner when the vehicle-received modal
                    // is open — the error is displayed inside the modal itself.
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700">
                        <AlertCircle size={18} />
                        {operationError || bookingsError}
                    </div>
                )}

                {/* Calendar Controls */}
                <div className="bg-white rounded-2xl border border-gray-200 mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => dispatch(navigatePrevious())}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ChevronLeft size={20} className="text-gray-600" />
                            </button>
                            <button
                                onClick={() => dispatch(navigateToToday())}
                                className="px-3 py-1.5 text-sm font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                            >
                                Today
                            </button>
                            <button
                                onClick={() => dispatch(navigateNext())}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ChevronRight size={20} className="text-gray-600" />
                            </button>
                            <span className="ml-2 text-lg font-semibold text-gray-900">
                                {formatDate(currentDate)}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Phone bookings live OUTSIDE this grid. A caller the bot turned
                                away never becomes a booking, so it can never appear on a
                                calendar — yet it is exactly the thing a workshop needs to see.
                                Hence a drawer here rather than another view mode. */}
                            {/* Bookings the bot made INSIDE this view — a side panel, so it
                                can be read against the grid rather than covering it. */}
                            <button
                                type="button"
                                onClick={() => setBotPanelOpen(o => !o)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors ${
                                    botPanelOpen
                                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                                }`}
                                title="Bookings the AI line made in this period"
                            >
                                <Phone size={15} />
                                Bot
                                {phoneCallCount > 0 && (
                                    <span className="ml-0.5 rounded-full bg-emerald-600 px-1.5 text-xs font-semibold text-white">
                                        {phoneCallCount}
                                    </span>
                                )}
                            </button>

                            {/* Calls that never became bookings live over 30 days, not in
                                this range, so they get a full drawer of their own. */}
                            <button
                                type="button"
                                onClick={() => setPhoneDrawerOpen(true)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                title="All calls taken by the AI booking line, including missed ones"
                            >
                                Missed calls
                            </button>

                            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
                                {['day', 'week', 'month'].map((mode) => (
                                    <button
                                        key={mode}
                                        onClick={() => dispatch(setViewMode(mode))}
                                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors capitalize ${
                                            viewMode === mode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                    >
                                        {mode}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {bookingsLoading && (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 size={32} className="animate-spin text-emerald-600" />
                        </div>
                    )}

                    {/* Calendar and the bot panel share a row: the panel is a side
                        drawer on the calendar, not an overlay over it. min-w-0 lets the
                        calendar shrink instead of forcing the row to overflow. */}
                    <div className="flex items-start gap-0">
                    <div className="flex-1 min-w-0">
                    {!bookingsLoading && viewMode === 'day' && (
                        <DayView
                            date={new Date(currentDate)}
                            bookings={bookings}
                            getBookingsForSlot={getBookingsForSlot}
                            onCellClick={handleCellClick}
                            onBookingClick={handleBookingClick}
                            onDragStart={handleDragStart}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onDragEnd={handleDragEnd}
                            draggingBooking={draggingBooking}
                            dropTarget={dropTarget}
                        />
                    )}

                    {!bookingsLoading && viewMode === 'week' && (
                        <WeekView
                            currentDate={new Date(currentDate)}
                            bookings={bookings}
                            getBookingsForSlot={getBookingsForSlot}
                            onCellClick={handleCellClick}
                            onBookingClick={handleBookingClick}
                            onDragStart={handleDragStart}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onDragEnd={handleDragEnd}
                            draggingBooking={draggingBooking}
                            dropTarget={dropTarget}
                        />
                    )}

                    {!bookingsLoading && viewMode === 'month' && (
                        <MonthView
                            currentDate={new Date(currentDate)}
                            bookings={bookings}
                            getBookingsForDate={getBookingsForDate}
                            onCellClick={handleCellClick}
                            onBookingClick={handleBookingClick}
                            onDragStart={handleDragStart}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onDragEnd={handleDragEnd}
                            draggingBooking={draggingBooking}
                            dropTarget={dropTarget}
                        />
                    )}
                    </div>

                    <BotBookingsPanel
                        open={botPanelOpen}
                        onClose={() => setBotPanelOpen(false)}
                        onBook={handleBookPreBooking}
                    />
                    </div>
                </div>

                {/* ── Modals ── */}

                {/*
                 * CREATE — multi-step flow:
                 *   Step 1: customer lookup by phone/email
                 *   Step 2: customer & vehicle (pre-filled read-only OR editable)
                 *   Step 3: booking details
                 *
                 * modalContext carries { date, time } pre-filled when user clicks
                 * a calendar cell, so step 3 opens with the slot pre-selected.
                 */}
                {showCreateModal && (
                    <BookingFormModal
                        isEdit={false}
                        initialData={modalContext}
                        bookingServiceCategories={bookingServiceCategories}
                        metaLoading={metaLoading}
                        loading={operationLoading}
                        error={operationError}
                        onClose={() => {
                            // Abandoning the form must NOT clear the queue row — the
                            // customer still has not been booked.
                            setPendingPreBooking(null);
                            dispatch(closeCreateModal());
                        }}
                        onSubmit={async (data) => {
                            const result = await dispatch(createBooking(data));
                            await settlePreBooking(result);
                            return result;
                        }}
                        onSubmitServiceProfile={async (data) => {
                            const result = await dispatch(createServiceProfileBooking(data));
                            await settlePreBooking(result);
                            return result;
                        }}
                        onSubmitMileage={async (data) => {
                            const result = await dispatch(createMileageBooking(data));
                            await settlePreBooking(result);
                            return result;
                        }}
                    />
                )}

                {/*
                 * EDIT — 2-step flow (lookup skipped):
                 *   Step 2: customer & vehicle pre-filled read-only (UUIDs resolved)
                 *   Step 3: booking details + loan car allocation panel
                 *
                 * existingBooking provides the full booking object so the modal
                 * can pre-populate every field and send UUIDs on submit.
                 *
                 * loanCarIncidents and loanCarIncidentsLoading are fetched eagerly
                 * in the onEdit callback (below) when booking.hasLoanCar is true,
                 * mirroring the same fetch that happens when the view modal opens.
                 */}
                {showEditModal && selectedBooking && (
                    <BookingFormModal
                        isEdit={true}
                        existingBooking={selectedBooking}
                        bookingServiceCategories={bookingServiceCategories}
                        metaLoading={metaLoading}
                        loading={operationLoading}
                        error={operationError}
                        onClose={() => dispatch(closeEditModal())}
                        onSubmit={(data) => dispatch(updateBooking({ uuid: selectedBooking.uuid, data }))}
                        loanCarIncidents={loanCarIncidents}
                        loanCarIncidentsLoading={loanCarIncidentsLoading}
                    />
                )}

                {showViewModal && selectedBooking && (
                    <BookingDetailModal
                        booking={selectedBooking}
                        bookingServiceCategories={bookingServiceCategories}
                        loanCarIncidents={loanCarIncidents}
                        loanCarIncidentsLoading={loanCarIncidentsLoading}
                        onClose={() => dispatch(closeViewModal())}
                        onViewJob={() => handleViewJob(selectedBooking.jobUuid)}
                        onEdit={() => {
                            dispatch(closeViewModal());
                            dispatch(openEditModal(selectedBooking));
                            // Fetch handover incidents for the edit modal's loan car panel,
                            // reusing whatever is already in state if the view modal just
                            // loaded them — the slice clears stale data on openEditModal.
                            if (selectedBooking.hasLoanCar) {
                                dispatch(fetchLoanCarIncidents(selectedBooking.uuid));
                            }
                        }}
                        onCancel={() => {
                            dispatch(closeViewModal());
                            dispatch(openCancelConfirm(selectedBooking));
                        }}
                        onStatusChange={(status) => handleStatusChange(selectedBooking, status)}
                        /*
                         * Inline-edit handler for the drop-off / pickup person tiles.
                         * Accepts a partial-fields object so each tile only sends the
                         * two fields it owns; the rest of the booking body is rebuilt
                         * from the current booking via buildUpdateBodyFromBooking,
                         * which keeps the PUT idempotent for everything else.
                         *
                         * Dispatches updateBookingPersons (not updateBooking) so the
                         * view modal stays open after a successful save — the slice's
                         * fulfilled handler refreshes selectedBooking in place rather
                         * than nulling it.
                         *
                         * Returns { error } on a non-throwing failure so the editor
                         * can show inline feedback without the user losing their draft.
                         */
                        onSavePersons={async (partial) => {
                            const body = buildUpdateBodyFromBooking(selectedBooking, partial);
                            const result = await dispatch(updateBookingPersons({
                                uuid: selectedBooking.uuid,
                                data: body,
                            }));
                            if (result.meta?.requestStatus !== 'fulfilled') {
                                return { error: result.payload || 'Failed to save.' };
                            }
                            return null;
                        }}
                        /*
                         * Generic inline-edit handler for booking fields exposed via
                         * the detail panel (date/time and services). Same contract as
                         * onSavePersons — accepts a partial object, rebuilds the full
                         * PUT body via buildUpdateBodyFromBooking, dispatches the
                         * stay-open updateBookingPersons thunk (the endpoint is the
                         * same; the thunk just doesn't close the modal on success),
                         * and returns { error } on failure so the editor can surface
                         * inline feedback without losing the user's draft.
                         */
                        onSaveBookingFields={async (partial) => {
                            const body = buildUpdateBodyFromBooking(selectedBooking, partial);
                            const result = await dispatch(updateBookingPersons({
                                uuid: selectedBooking.uuid,
                                data: body,
                            }));
                            if (result.meta?.requestStatus !== 'fulfilled') {
                                return { error: result.payload || 'Failed to save.' };
                            }
                            return null;
                        }}
                        loading={operationLoading}
                    />
                )}

                {/*
                 * ── Inline JobDetailModal ─────────────────────────────────
                 * Rendered ON TOP of BookingDetailModal when staff click
                 * "View Job" in the booking detail view. onBack returns to
                 * the booking modal (it's still mounted underneath) rather
                 * than closing the whole stack. onClose closes both —
                 * matches the existing X-icon close behaviour on every
                 * other modal in the app.
                 *
                 * Handler subset:
                 *   • Safe lifecycle (start / pause / resume) — direct dispatch.
                 *   • Work item add/update/remove — direct dispatch.
                 *   • Add note / refresh — direct dispatch.
                 *   • Resource assign / unassign — direct dispatch then refresh
                 *     of jobResources + availableResources to keep both panels
                 *     in sync.
                 *   • Complete / Vehicle Collected / Assign Staff — these
                 *     require dedicated confirm modals that live on JobsPage;
                 *     here they close the job modal and inform the user to
                 *     use the Jobs page for those actions. The buttons only
                 *     surface in COMPLETED/INVOICED/CREATED/QUOTED/APPROVED
                 *     status anyway, so most bookings never hit them.
                 */}
                {viewingJobUuid && selectedJob && selectedJob.uuid === viewingJobUuid && (
                    <JobDetailModal
                        onOpenInvoice={openInvoice}
                        job={selectedJob}
                        operationLoading={jobOperationLoading}
                        detailLoading={jobDetailLoading}
                        operationError={jobOperationError}
                        onClearError={() => dispatch(clearJobOperationStatus())}
                        jobResources={jobResources}
                        availableResources={availableResources}
                        resourcesLoading={resourcesLoading}
                        workCategories={workCategories}
                        categoryTypes={jobCategoryTypes}
                        onClose={() => {
                            setViewingJobUuid(null);
                            dispatch(closeViewModal());
                        }}
                        onBack={() => setViewingJobUuid(null)}
                        backLabel="Back to Booking"
                        onStart={(job) => dispatch(startJob(job.uuid))}
                        onPause={(job) => dispatch(pauseJob({ uuid: job.uuid, reason: null }))}
                        onResume={(job) => dispatch(resumeJob(job.uuid))}
                        onOpenComplete={() => {
                            // Complete-job confirm dialog lives on JobsPage; send
                            // the user there to finish the transition.
                            setViewingJobUuid(null);
                            window.alert('Open the Jobs page to mark this job complete.');
                        }}
                        onOpenVehicleCollect={() => {
                            // Vehicle-collected modal lives on JobsPage.
                            setViewingJobUuid(null);
                            window.alert('Open the Jobs page to record vehicle collection.');
                        }}
                        onOpenAssign={(job) => {
                            // Open the inline AssignStaffPicker stacked above
                            // JobDetailModal. The picker fetches staff on
                            // mount and dispatches assignStaff on select.
                            setAssigningJobUuid(job.uuid);
                        }}
                        onAddWorkItem={(data) =>
                            dispatch(addWorkItem({ jobUuid: selectedJob.uuid, data }))
                        }
                        onEditWorkItem={(data, workItemUuid) =>
                            dispatch(updateWorkItem({
                                jobUuid: selectedJob.uuid,
                                workItemUuid,
                                data,
                            }))
                        }
                        onRemoveWorkItem={(workItemUuid) =>
                            dispatch(removeWorkItem({
                                jobUuid: selectedJob.uuid,
                                workItemUuid,
                            }))
                        }
                        onAddNote={async (data) => {
                            // IMPORTANT: JobNotesTab awaits this and reads `.notes` off the
                            // returned job to find the just-created note (for image upload).
                            // We MUST return the thunk's payload, not the raw dispatch envelope
                            // ({ type, payload, meta }) — otherwise image uploads silently skip
                            // because `updatedJob.notes` is undefined. Mirrors handleAddNoteFromTab
                            // in JobsPage.jsx and JobsTabletView.jsx.
                            const result = await dispatch(addJobNote({
                                jobUuid: selectedJob.uuid,
                                data,
                            }));
                            if (addJobNote.fulfilled.match(result)) return result.payload;
                            throw new Error(result.payload || 'Failed to add note');
                        }}
                        onRefreshJob={async () => {
                            // Same unwrap pattern — keeps the contract with JobNotesTab /
                            // any consumer that expects the job object back.
                            const result = await dispatch(fetchJob(selectedJob.uuid));
                            if (fetchJob.fulfilled.match(result)) return result.payload;
                            return null;
                        }}
                        onAssignResource={(resource) =>
                            dispatch(assignResource({
                                jobUuid: selectedJob.uuid,
                                data: { resourceUuid: resource.uuid },
                            }))
                        }
                        onUnassignResource={(resource) =>
                            dispatch(unassignResource({
                                jobUuid: selectedJob.uuid,
                                resourceUuid: resource.uuid,
                            }))
                        }
                    />
                )}

                {/*
                 * ── Inline AssignStaffPicker ──────────────────────────────
                 * Stacked above JobDetailModal when staff click "Assign" from
                 * inside it. Self-fetches the staff list, dispatches
                 * assignStaff on select, then closes itself — JobDetailModal's
                 * fulfilled handler refreshes selectedJob so the "ASSIGNED TO"
                 * tile updates in place without a manual re-fetch.
                 */}
                {assigningJobUuid && (
                    <AssignStaffPicker
                        jobUuid={assigningJobUuid}
                        loading={jobOperationLoading}
                        onClose={() => setAssigningJobUuid(null)}
                        onSelect={async (staffUuid) => {
                            const result = await dispatch(assignStaff({
                                uuid: assigningJobUuid,
                                staffUuid,
                            }));
                            if (result.meta?.requestStatus === 'fulfilled') {
                                // Refresh the job so the assignedStaff fields
                                // surface in the detail tiles immediately.
                                dispatch(fetchJob(assigningJobUuid));
                                setAssigningJobUuid(null);
                            }
                        }}
                    />
                )}

                {showCancelConfirm && selectedBooking && (
                    <CancelConfirmModal
                        booking={selectedBooking}
                        onClose={() => dispatch(closeCancelConfirm())}
                        onConfirm={() => dispatch(cancelBooking(selectedBooking.uuid))}
                        loading={operationLoading}
                    />
                )}

                {/*
                 * VEHICLE RECEIVED MODAL
                 *
                 * Always shown when staff click "VEHICLE RECEIVED" — the dedicated
                 * PATCH /bookings/{uuid}/vehicle-received endpoint now owns this
                 * transition regardless of whether a loan car is attached.
                 *
                 * The modal collects handover notes + photo S3 keys. The backend
                 * enforces that loanCarDetails is non-null when an active loan car
                 * allocation exists on the booking.
                 *
                 * operationError is surfaced inside the modal (not the page banner).
                 */}
                {showVehicleReceivedModal && selectedBooking && (
                    <VehicleReceivedModal
                        booking={selectedBooking}
                        imageCategories={loanCarImageCategories}
                        onConfirm={handleVehicleReceivedConfirm}
                        onClose={() => dispatch(closeVehicleReceivedModal())}
                        loading={operationLoading}
                        error={operationError}
                    />
                )}

                {/* Pre-vehicle-received pickup confirmation popup. Always shown
                    before the transition fires, regardless of loan-car presence.
                    On confirm: optionally PUTs the booking with updated pickup
                    person, then either fires markVehicleReceived directly (no
                    loan car) or opens VehicleReceivedModal (loan car). */}
                {pickupConfirmFor && (
                    <PickupConfirmModal
                        booking={pickupConfirmFor}
                        loading={operationLoading}
                        onClose={() => setPickupConfirmFor(null)}
                        onConfirm={handlePickupConfirm}
                    />
                )}

                <PhoneBookingsDrawer
                    open={phoneDrawerOpen}
                    onClose={() => setPhoneDrawerOpen(false)}
                />
            </div>
        </Layout>
    );
};

// ==================== Summary Badge Component (Compact) ====================

const SummaryBadge = ({ label, value, color }) => {
    const colorClasses = {
        blue: 'bg-blue-100 text-blue-700 border-blue-200',
        emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        gray: 'bg-gray-100 text-gray-700 border-gray-200',
        amber: 'bg-amber-100 text-amber-700 border-amber-200',
        red: 'bg-red-100 text-red-700 border-red-200',
    };

    return (
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-sm whitespace-nowrap ${colorClasses[color]}`}>
            <span className="font-bold">{value}</span>
            <span className="opacity-75">{label}</span>
        </div>
    );
};

// ==================== Day View Component ====================

/**
 * The day view renders TIME_SLOTS as a fixed-height grid of drop targets,
 * then overlays bookings as absolutely-positioned blocks that span their
 * full `estimatedDurationMinutes`. Each booking gets a distinct colour
 * from BOOKING_PALETTE so two concurrent jobs can be told apart at a
 * glance. Overlapping bookings are laid out side-by-side within the
 * timeline column.
 */
const DayView = ({
                     date, bookings, getBookingsForSlot, onCellClick, onBookingClick,
                     onDragStart, onDragOver, onDrop, onDragEnd, draggingBooking, dropTarget
                 }) => {
    const dateStr = toISODateString(date);

    // All bookings for this day (regardless of slot), sorted by start time —
    // used to compute absolute positions and assign palette colours.
    const dayBookings = sortBookingsByTime(
        bookings.filter(b => b.scheduledDate === dateStr)
    );

    // Lay out overlapping bookings in side-by-side columns. Greedy sweep:
    // each booking takes the leftmost column whose last occupant ended
    // before this booking's start. `totalCols` is the max column index used.
    const laidOut = [];
    const columnEndMinutes = []; // end-minute per column index
    dayBookings.forEach((b) => {
        const start = timeToSlotOffsetMinutes(b.scheduledTime);
        if (start == null) return; // outside visible range — skip
        const duration = getBookingDurationMinutes(b);
        const end = start + duration;

        let col = columnEndMinutes.findIndex(e => e <= start);
        if (col === -1) {
            col = columnEndMinutes.length;
            columnEndMinutes.push(end);
        } else {
            columnEndMinutes[col] = end;
        }
        laidOut.push({ booking: b, start, duration, col });
    });
    const totalCols = Math.max(1, columnEndMinutes.length);

    return (
        <div className="overflow-x-auto">
            <div className="min-w-[400px] flex">
                {/* Time gutter */}
                <div className="w-20 shrink-0 border-r border-gray-100">
                    {TIME_SLOTS.map((time) => (
                        <div
                            key={time}
                            style={{ height: `${SLOT_HEIGHT_PX}px` }}
                            className="px-4 py-3 text-sm font-medium text-gray-500 border-b border-gray-100"
                        >
                            {time}
                        </div>
                    ))}
                </div>

                {/* Timeline column — slot grid underneath + absolute booking blocks on top */}
                <div className="flex-1 relative">
                    {/* Slot cells for clicking empty space / drop targets */}
                    {TIME_SLOTS.map((time) => {
                        const isDropTarget = dropTarget?.date === dateStr && dropTarget?.time === time;
                        return (
                            <div
                                key={time}
                                style={{ height: `${SLOT_HEIGHT_PX}px` }}
                                className={`border-b border-gray-100 transition-colors cursor-pointer ${
                                    isDropTarget
                                        ? 'bg-emerald-100 ring-2 ring-emerald-400 ring-inset'
                                        : 'hover:bg-gray-50'
                                }`}
                                onClick={() => onCellClick(date, time)}
                                onDragOver={(e) => onDragOver(e, date, time)}
                                onDragLeave={(e) => onDragOver(e, null, null)}
                                onDrop={() => onDrop(date, time)}
                            >
                                {isDropTarget && draggingBooking && (
                                    <div className="text-xs text-emerald-600 font-medium px-3 pt-1 flex items-center gap-1">
                                        <span>↓</span> Drop to reschedule to {time}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Booking blocks — absolutely positioned over the slot grid */}
                    {laidOut.map(({ booking, start, duration, col }) => {
                        // Palette index follows the sorted order of the day's bookings
                        const dayIdx = dayBookings.findIndex(b => b.uuid === booking.uuid);
                        const scheme = getSchemeByIndex(dayIdx);

                        const top = (start / SLOT_MINUTES) * SLOT_HEIGHT_PX;
                        const height = Math.max(MIN_BLOCK_HEIGHT_PX, (duration / SLOT_MINUTES) * SLOT_HEIGHT_PX - 4);
                        const widthPct = 100 / totalCols;
                        const leftPct = col * widthPct;

                        return (
                            <BookingBlock
                                key={booking.uuid}
                                booking={booking}
                                scheme={scheme}
                                style={{
                                    position: 'absolute',
                                    top: `${top}px`,
                                    height: `${height}px`,
                                    left: `calc(${leftPct}% + 4px)`,
                                    width: `calc(${widthPct}% - 8px)`,
                                }}
                                onClick={() => onBookingClick(booking)}
                                onDragStart={() => onDragStart(booking)}
                                onDragEnd={onDragEnd}
                                isDragging={draggingBooking?.uuid === booking.uuid}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// ==================== Week View Component ====================

/**
 * Week view mirrors the day view's approach: each day column has a fixed
 * slot grid underneath and absolutely-positioned booking blocks on top
 * that span their full duration. Bookings within the same day get distinct
 * palette colours (sorted by start time).
 */
const WeekView = ({
                      currentDate, bookings, getBookingsForSlot, onCellClick, onBookingClick,
                      onDragStart, onDragOver, onDrop, onDragEnd, draggingBooking, dropTarget
                  }) => {
    const weekDates = getWeekDates(currentDate);
    const gridHeight = TIME_SLOTS.length * SLOT_HEIGHT_PX;

    return (
        <div className="overflow-x-auto">
            <div className="min-w-[800px]">
                {/* Day headers */}
                <div className="grid grid-cols-8 border-b border-gray-200">
                    <div className="p-3 text-sm font-medium text-gray-500"></div>
                    {weekDates.map((date, idx) => {
                        const isToday = toISODateString(date) === toISODateString(new Date());
                        return (
                            <div
                                key={idx}
                                className={`p-3 text-center border-l border-gray-100 ${isToday ? 'bg-emerald-50' : ''}`}
                            >
                                <p className="text-xs text-gray-500 uppercase">
                                    {date.toLocaleDateString('en-AU', { weekday: 'short' })}
                                </p>
                                <p className={`text-lg font-semibold ${isToday ? 'text-emerald-600' : 'text-gray-900'}`}>
                                    {date.getDate()}
                                </p>
                            </div>
                        );
                    })}
                </div>

                {/* Timeline — gutter + 7 day columns, all at the same fixed height */}
                <div className="grid grid-cols-8" style={{ height: `${gridHeight}px` }}>
                    {/* Time gutter */}
                    <div className="border-r border-gray-100">
                        {TIME_SLOTS.map((time) => (
                            <div
                                key={time}
                                style={{ height: `${SLOT_HEIGHT_PX}px` }}
                                className="px-2 py-2 text-xs text-gray-500 font-medium border-b border-gray-100"
                            >
                                {time}
                            </div>
                        ))}
                    </div>

                    {/* Day columns */}
                    {weekDates.map((date, dayIdx) => {
                        const dateStr = toISODateString(date);
                        const dayBookings = sortBookingsByTime(
                            bookings.filter(b => b.scheduledDate === dateStr)
                        );

                        // Greedy column assignment for overlapping bookings
                        const laidOut = [];
                        const columnEndMinutes = [];
                        dayBookings.forEach((b) => {
                            const start = timeToSlotOffsetMinutes(b.scheduledTime);
                            if (start == null) return;
                            const duration = getBookingDurationMinutes(b);
                            const end = start + duration;
                            let col = columnEndMinutes.findIndex(e => e <= start);
                            if (col === -1) {
                                col = columnEndMinutes.length;
                                columnEndMinutes.push(end);
                            } else {
                                columnEndMinutes[col] = end;
                            }
                            laidOut.push({ booking: b, start, duration, col });
                        });
                        const totalCols = Math.max(1, columnEndMinutes.length);

                        return (
                            <div key={dayIdx} className="relative border-l border-gray-100">
                                {/* Slot grid underneath (drop targets) */}
                                {TIME_SLOTS.map((time) => {
                                    const isDropTarget = dropTarget?.date === dateStr && dropTarget?.time === time;
                                    return (
                                        <div
                                            key={time}
                                            style={{ height: `${SLOT_HEIGHT_PX}px` }}
                                            className={`border-b border-gray-100 transition-colors cursor-pointer ${
                                                isDropTarget
                                                    ? 'bg-emerald-100 ring-2 ring-emerald-400 ring-inset'
                                                    : 'hover:bg-gray-50'
                                            }`}
                                            onClick={() => onCellClick(date, time)}
                                            onDragOver={(e) => onDragOver(e, date, time)}
                                            onDragLeave={(e) => onDragOver(e, null, null)}
                                            onDrop={() => onDrop(date, time)}
                                        >
                                            {isDropTarget && draggingBooking && (
                                                <div className="text-[10px] text-emerald-600 font-medium text-center pt-1">
                                                    ↓ Drop
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* Booking blocks overlay */}
                                {laidOut.map(({ booking, start, duration, col }) => {
                                    const paletteIdx = dayBookings.findIndex(b => b.uuid === booking.uuid);
                                    const scheme = getSchemeByIndex(paletteIdx);
                                    const top = (start / SLOT_MINUTES) * SLOT_HEIGHT_PX;
                                    const height = Math.max(MIN_BLOCK_HEIGHT_PX, (duration / SLOT_MINUTES) * SLOT_HEIGHT_PX - 4);
                                    const widthPct = 100 / totalCols;
                                    const leftPct = col * widthPct;

                                    return (
                                        <BookingBlock
                                            key={booking.uuid}
                                            booking={booking}
                                            scheme={scheme}
                                            compact
                                            style={{
                                                position: 'absolute',
                                                top: `${top}px`,
                                                height: `${height}px`,
                                                left: `calc(${leftPct}% + 2px)`,
                                                width: `calc(${widthPct}% - 4px)`,
                                            }}
                                            onClick={() => onBookingClick(booking)}
                                            onDragStart={() => onDragStart(booking)}
                                            onDragEnd={onDragEnd}
                                            isDragging={draggingBooking?.uuid === booking.uuid}
                                        />
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// ==================== Month View Component ====================

const MonthView = ({
                       currentDate, bookings, getBookingsForDate, onCellClick, onBookingClick,
                       onDragStart, onDragOver, onDrop, onDragEnd, draggingBooking, dropTarget
                   }) => {
    const monthDates = getMonthDates(currentDate);
    const currentMonth = currentDate.getMonth();

    return (
        <div className="p-4">
            <div className="grid grid-cols-7 mb-2">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                    <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                        {day}
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
                {monthDates.map((date, idx) => {
                    const isCurrentMonth = date.getMonth() === currentMonth;
                    const isToday = toISODateString(date) === toISODateString(new Date());
                    const dayBookings = sortBookingsByTime(getBookingsForDate(date));
                    const dateStr = toISODateString(date);
                    const isDropTarget = dropTarget?.date === dateStr;

                    // If a single booking consumes the full working day, render the
                    // cell as a solid block using that booking's palette colour.
                    const fullDayBooking = dayBookings.find(b =>
                        getBookingDurationMinutes(b) >= FULL_DAY_MINUTES
                    );
                    const fullDayScheme = fullDayBooking
                        ? getSchemeByIndex(dayBookings.findIndex(b => b.uuid === fullDayBooking.uuid))
                        : null;

                    return (
                        <div
                            key={idx}
                            className={`min-h-[100px] p-2 rounded-lg border cursor-pointer transition-all ${
                                isDropTarget
                                    ? 'bg-emerald-100 border-emerald-400 ring-2 ring-emerald-400 shadow-lg'
                                    : fullDayScheme
                                        ? `${fullDayScheme.bg} ${fullDayScheme.border}`
                                        : isCurrentMonth
                                            ? 'bg-white border-gray-200 hover:border-gray-300'
                                            : 'bg-gray-50 border-gray-100'
                            } ${isToday && !isDropTarget ? 'ring-2 ring-emerald-500' : ''}`}
                            onClick={() => onCellClick(date, '09:00')}
                            onDragOver={(e) => onDragOver(e, date, null)}
                            onDragLeave={(e) => onDragOver(e, null, null)}
                            onDrop={() => onDrop(date, '09:00')}
                        >
                            <p className={`text-sm font-medium mb-1 ${
                                isDropTarget
                                    ? 'text-emerald-700'
                                    : fullDayScheme
                                        ? fullDayScheme.text
                                        : isToday
                                            ? 'text-emerald-600'
                                            : isCurrentMonth
                                                ? 'text-gray-900'
                                                : 'text-gray-400'
                            }`}>
                                {date.getDate()}
                            </p>
                            {isDropTarget && draggingBooking && (
                                <div className="text-xs text-emerald-600 font-medium mb-1 bg-emerald-50 rounded px-1 py-0.5 text-center">
                                    ↓ Drop here
                                </div>
                            )}

                            {/* Full-day label when a booking takes the whole cell */}
                            {fullDayBooking && fullDayScheme && (
                                <div
                                    draggable
                                    onClick={(e) => { e.stopPropagation(); onBookingClick(fullDayBooking); }}
                                    onDragStart={() => onDragStart(fullDayBooking)}
                                    onDragEnd={onDragEnd}
                                    className={`text-xs font-semibold ${fullDayScheme.text} truncate`}
                                >
                                    ● {fullDayBooking.customerName} · all day
                                </div>
                            )}

                            {/* Remaining bookings render as coloured pills */}
                            <div className="space-y-1 mt-1">
                                {dayBookings
                                    .filter(b => b.uuid !== fullDayBooking?.uuid)
                                    .slice(0, 3)
                                    .map((booking) => {
                                        const paletteIdx = dayBookings.findIndex(b => b.uuid === booking.uuid);
                                        const scheme = getSchemeByIndex(paletteIdx);
                                        return (
                                            <BookingCard
                                                key={booking.uuid}
                                                booking={booking}
                                                scheme={scheme}
                                                minimal
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onBookingClick(booking);
                                                }}
                                                onDragStart={() => onDragStart(booking)}
                                                onDragEnd={onDragEnd}
                                                isDragging={draggingBooking?.uuid === booking.uuid}
                                            />
                                        );
                                    })}
                                {dayBookings.filter(b => b.uuid !== fullDayBooking?.uuid).length > 3 && (
                                    <p className="text-xs text-gray-500">
                                        +{dayBookings.filter(b => b.uuid !== fullDayBooking?.uuid).length - 3} more
                                    </p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ==================== Booking Card Component ====================

/**
 * Minimal / compact variants used by MonthView (and anywhere a booking needs
 * to render as a simple pill rather than a duration-spanning block). When a
 * `scheme` is provided, that palette is used; otherwise the status colour is
 * used so existing call sites still make sense.
 */
const BookingCard = ({ booking, onClick, onDragStart, onDragEnd, isDragging, compact, minimal, scheme }) => {
    const color = scheme
        ? `${scheme.bg} ${scheme.border} ${scheme.text}`
        : getStatusColor(booking.status);

    if (minimal) {
        return (
            <div
                draggable
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                onClick={onClick}
                className={`text-xs p-1 rounded border truncate cursor-pointer ${color} ${isDragging ? 'opacity-50' : ''}`}
            >
                {formatTime(booking.scheduledTime)} {booking.customerName?.split(' ')[0]}
            </div>
        );
    }

    if (compact) {
        return (
            <div
                draggable
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                onClick={onClick}
                className={`text-xs p-1.5 rounded border mb-1 cursor-pointer ${color} ${isDragging ? 'opacity-50' : ''}`}
            >
                <p className="font-medium truncate">{booking.customerName}</p>
                <p className="truncate opacity-75 flex items-center gap-1">
                    <span className="truncate">{booking.vehicleRego}</span>
                    <TransmissionBadge value={booking.vehicleTransmission} variant="icon" size={10} />
                </p>
            </div>
        );
    }

    return (
        <div
            draggable
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onClick={onClick}
            className={`p-3 rounded-lg border mb-2 cursor-pointer ${color} ${isDragging ? 'opacity-50' : ''}`}
        >
            <div className="flex items-start justify-between">
                <div>
                    <p className="font-semibold">{booking.customerName}</p>
                    <p className="text-sm opacity-75 flex items-center gap-2">
                        <span className="truncate">{booking.vehicleDescription || booking.vehicleRego}</span>
                        <TransmissionBadge value={booking.vehicleTransmission} variant="icon" size={12} />
                    </p>
                    {(() => {
                        const types = getBookingServiceTypes(booking);
                        if (types.length === 0) return null;
                        const first = formatServiceTypeLabel(types[0].type);
                        const more = types.length - 1;
                        return (
                            <p className="text-xs mt-1">
                                {first}
                                {more > 0 && <span className="opacity-70"> +{more} more</span>}
                            </p>
                        );
                    })()}
                </div>
                <GripVertical size={16} className="opacity-50" />
            </div>
        </div>
    );
};

// ==================== Booking Block (duration-spanning) ====================

/**
 * A booking rendered as an absolutely-positioned block inside the Day or Week
 * timeline. Height is set by the caller based on estimatedDurationMinutes.
 * The block shows as much detail as vertical space allows:
 *   - very short (< 60min): just the name
 *   - short  (60-90min): name + rego
 *   - tall   (>= 90min):  name + vehicle + service type + duration summary
 *
 * A coloured bar on the left edge encodes the booking's palette hue so
 * concurrent bookings are easy to distinguish even at a glance. A status
 * badge in the top-right preserves the existing status signal.
 */
// Tooltip box size — needed up front to decide which side of the block it goes on.
const TOOLTIP_WIDTH_PX = 244;
const TOOLTIP_GAP_PX = 8;
const TOOLTIP_MAX_HEIGHT_PX = 220;

/**
 * Hover card for a booking block: the two things a block is too small to show —
 * what work is booked in, and whether a loan car is involved.
 *
 * <p>Loan car state is read from {@code loanCarSummary} rather than the
 * {@code hasLoanCar} flag. The flag is a backward-compat shim the backend derives
 * from the summary being non-null, and it goes stale on a booking whose loan car
 * was allocated after the booking was loaded — the summary is the real source.
 */
const BookingTooltip = ({ booking, duration, pos }) => {
    const types = getBookingServiceTypes(booking);
    const loanCar = booking.loanCarSummary;

    return (
        <div
            role="tooltip"
            style={{
                position: 'fixed',
                left: `${pos.left}px`,
                top: `${pos.top}px`,
                width: `${TOOLTIP_WIDTH_PX}px`,
            }}
            className="z-50 pointer-events-none rounded-lg bg-gray-900 text-white shadow-xl p-2.5 text-left"
        >
            <p className="text-xs font-semibold leading-tight">
                {formatTime(booking.scheduledTime)} · {booking.customerName}
            </p>
            <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                <Clock size={9} />
                {formatDurationShort(duration)}
                {booking.vehicleRego && <span className="font-mono">· {booking.vehicleRego}</span>}
            </p>

            {/* Service types — every row, with its own duration where known. */}
            <div className="mt-2 pt-2 border-t border-white/10">
                <p className="text-[9px] uppercase tracking-wide text-gray-400 mb-1">Service</p>
                {types.length === 0 ? (
                    <p className="text-[11px] text-gray-400 italic">No service type recorded</p>
                ) : (
                    <ul className="space-y-0.5">
                        {types.map((t, i) => {
                            const rowDuration = formatDurationShort(t.durationMinutes);
                            return (
                                <li key={i} className="text-[11px] flex items-start justify-between gap-2">
                                    <span className="truncate">
                                        {booking.serviceProfileName || formatServiceTypeLabel(t.type)}
                                    </span>
                                    {rowDuration && (
                                        <span className="shrink-0 text-gray-400">{rowDuration}</span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {/* Loan car — always shown, including the negative case. "No loan car"
                is information: it is what tells staff no handover is due. */}
            <div className="mt-2 pt-2 border-t border-white/10">
                <p className="text-[9px] uppercase tracking-wide text-gray-400 mb-1">Loan car</p>
                {loanCar ? (
                    <>
                        <p className="text-[11px] flex items-center gap-1.5">
                            <Car size={10} className="text-emerald-400 shrink-0" />
                            <span className="font-mono">{loanCar.rego}</span>
                        </p>
                        {loanCar.allocationEnd && (
                            <p className="text-[10px] text-gray-400 mt-0.5">
                                Due back {formatDateTime(loanCar.allocationEnd)}
                            </p>
                        )}
                    </>
                ) : (
                    <p className="text-[11px] text-gray-400">No loan car</p>
                )}
            </div>
        </div>
    );
};

const BookingBlock = ({ booking, scheme, style, onClick, onDragStart, onDragEnd, isDragging, compact }) => {
    const duration = getBookingDurationMinutes(booking);
    const heightPx = parseFloat(style?.height ?? '0');
    const showVehicle = heightPx >= 44;
    const showService = heightPx >= 72 && !compact;
    const showFooter = heightPx >= 90 && !compact;

    const hours = Math.floor(duration / 60);
    const mins = duration % 60;
    const durationLabel = hours > 0
        ? `${hours}h${mins > 0 ? ` ${mins}m` : ''}`
        : `${mins}m`;

    // Hover tooltip. Opens on mouseenter with no delay — staff scan the diary,
    // and a delayed tip means hovering three blocks to find one costs three
    // waits. Anchored off the block's rect and rendered `fixed`, because the day
    // columns scroll horizontally and an absolutely-positioned tip inside the
    // block would be clipped at the column edge.
    const [tipPos, setTipPos] = useState(null);

    const openTip = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const spillsRight = r.right + TOOLTIP_GAP_PX + TOOLTIP_WIDTH_PX > window.innerWidth;
        // Flip left near the Sunday column, and lift off the bottom edge for the
        // late-afternoon rows. TOOLTIP_MAX_HEIGHT_PX is an upper estimate — the
        // card grows with the number of service rows, so clamping on a measured
        // height would need a second render pass to place it.
        const maxTop = window.innerHeight - TOOLTIP_MAX_HEIGHT_PX - TOOLTIP_GAP_PX;
        setTipPos({
            left: spillsRight ? r.left - TOOLTIP_GAP_PX - TOOLTIP_WIDTH_PX : r.right + TOOLTIP_GAP_PX,
            top: Math.max(TOOLTIP_GAP_PX, Math.min(r.top, maxTop)),
        });
    };

    // Dragging a block would otherwise leave the tip stranded mid-screen.
    const closeTip = () => setTipPos(null);

    return (
        <div
            draggable
            onDragStart={(e) => { closeTip(); onDragStart?.(e); }}
            onDragEnd={onDragEnd}
            onMouseEnter={openTip}
            onMouseLeave={closeTip}
            onClick={(e) => { e.stopPropagation(); closeTip(); onClick?.(e); }}
            style={style}
            className={`rounded-lg border overflow-hidden cursor-pointer shadow-sm hover:shadow-md transition-shadow flex ${
                scheme.bg
            } ${scheme.border} ${scheme.text} ${isDragging ? 'opacity-50' : ''}`}
        >
            {tipPos && <BookingTooltip booking={booking} duration={duration} pos={tipPos} />}

            {/* Colour stripe on the left edge */}
            <div className={`w-1.5 shrink-0 ${scheme.bar}`} />

            {/* Body */}
            <div className="flex-1 min-w-0 p-2 flex flex-col">
                <div className="flex items-start justify-between gap-1">
                    <p className="font-semibold text-xs leading-tight truncate">
                        {formatTime(booking.scheduledTime)} · {booking.customerName}
                    </p>
                    <span className={`shrink-0 text-[9px] px-1 py-0.5 rounded font-medium ${getStatusBadgeColor(booking.status)}`}>
                        {booking.status === 'VEHICLE_RECEIVED' ? 'IN' : booking.status.charAt(0)}
                    </span>
                </div>

                {showVehicle && (
                    <p className="text-[11px] opacity-80 truncate mt-0.5 font-mono flex items-center gap-1">
                        <span className="truncate">{booking.vehicleRego || booking.vehicleDescription}</span>
                        <TransmissionBadge value={booking.vehicleTransmission} variant="icon" size={10} />
                    </p>
                )}

                {showService && (() => {
                    const types = getBookingServiceTypes(booking);
                    if (types.length === 0) return null;
                    const first = formatServiceTypeLabel(types[0].type);
                    const more = types.length - 1;
                    return (
                        <p className="text-[11px] opacity-70 truncate mt-0.5">
                            {first}{more > 0 ? ` +${more} more` : ''}
                        </p>
                    );
                })()}

                {showFooter && (
                    <div className="mt-auto pt-1 flex items-center gap-1 text-[10px] opacity-70">
                        <Clock size={10} />
                        <span>{durationLabel}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

// ==================== Booking Detail Modal ====================

// Category icon for incident images
const IncidentCategoryIcon = ({ category, size = 13 }) => {
    switch (category) {
        case 'DRIVING_LICENCE':                return <CreditCard size={size} />;
        case 'CAR_DASHBOARD_ON_VEHICLE_START': return <LayoutDashboard size={size} />;
        case 'INSURANCE_CERTIFICATE':          return <Shield size={size} />;
        default:                               return <Paperclip size={size} />;
    }
};

const categoryLabel = (cat) => {
    switch (cat) {
        case 'DRIVING_LICENCE':                return 'Driving Licence';
        case 'CAR_DASHBOARD_ON_VEHICLE_START': return 'Dashboard';
        case 'INSURANCE_CERTIFICATE':          return 'Insurance Certificate';
        default:                               return 'Other';
    }
};

// ── Single incident card (OUT or IN) ────────────────────────────────────────
const IncidentCard = ({ incident, outOdometer }) => {
    const isOut = incident.type === 'OUT';

    // Show km travelled on the IN card when both odometer readings are available
    const kmDelta = !isOut && outOdometer != null && incident.odometerReading != null
        ? incident.odometerReading - outOdometer
        : null;

    return (
        <div className={`rounded-xl border p-4 space-y-3 ${isOut ? 'border-amber-200 bg-amber-50/40' : 'border-emerald-200 bg-emerald-50/40'}`}>

            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {isOut
                        ? <ArrowUpRight size={15} className="text-amber-600" />
                        : <ArrowDownLeft size={15} className="text-emerald-600" />
                    }
                    <span className={`text-sm font-bold ${isOut ? 'text-amber-700' : 'text-emerald-700'}`}>
                        {isOut ? 'Loan Car Out' : 'Loan Car In'}
                    </span>
                    <span className="text-xs text-gray-400 font-mono">{incident.rego}</span>
                </div>
                <span className="text-xs text-gray-400">
                    {new Date(incident.recordedAt).toLocaleString('en-AU', {
                        day: '2-digit', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                    })}
                </span>
            </div>

            {/* Odometer + km delta + Insurance */}
            <div className="flex items-center gap-3 text-sm flex-wrap">
                {incident.odometerReading != null && (
                    <div className="flex items-center gap-1.5 text-gray-600">
                        <Gauge size={13} className="text-gray-400" />
                        <span className="font-medium">{incident.odometerReading.toLocaleString()} km</span>
                    </div>
                )}
                {kmDelta != null && (
                    <div className="flex items-center gap-1 text-violet-700 bg-violet-50 border border-violet-200 rounded-md px-2 py-0.5">
                        <Hash size={11} className="text-violet-400" />
                        <span className="text-xs font-semibold">{kmDelta.toLocaleString()} km travelled</span>
                    </div>
                )}
                <div className="flex items-center gap-1.5 text-gray-600">
                    <Shield size={13} className={incident.insuranceTaken ? 'text-emerald-500' : 'text-gray-300'} />
                    <span>{incident.insuranceTaken ? 'Insurance taken' : 'No insurance'}</span>
                </div>
            </div>

            {/* Notes */}
            {incident.notes && (
                <p className="text-xs text-gray-600 bg-white rounded-lg px-3 py-2 border border-gray-100 leading-relaxed">
                    {incident.notes}
                </p>
            )}

            {/* Images by category */}
            {incident.imagesByCategory && incident.imagesByCategory.length > 0 && (
                <div className="space-y-2">
                    {incident.imagesByCategory.map((catGroup) => (
                        catGroup.images && catGroup.images.length > 0 && (
                            <div key={catGroup.category}>
                                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1 mb-1.5">
                                    <IncidentCategoryIcon category={catGroup.category} />
                                    {categoryLabel(catGroup.category)}
                                    <span className="font-normal normal-case text-gray-400">
                                        ({catGroup.images.length})
                                    </span>
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {catGroup.images.map((img, idx) => (
                                        <a
                                            key={idx}
                                            href={img.presignedUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block w-16 h-16 rounded-lg overflow-hidden border border-gray-200 shadow-sm hover:ring-2 hover:ring-emerald-400 transition-all shrink-0"
                                            title="Open full image"
                                        >
                                            <img
                                                src={img.presignedUrl}
                                                alt={categoryLabel(catGroup.category)}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'flex';
                                                }}
                                            />
                                            <div className="w-full h-full bg-gray-100 items-center justify-center text-gray-400 hidden">
                                                <FileImage size={18} />
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )
                    ))}
                </div>
            )}
        </div>
    );
};


/**
 * InlinePersonEditor
 *
 * Compact name + mobile tile used inside BookingDetailModal for the Drop-off
 * and Pickup person fields. Renders in two modes:
 *
 *   • Display — shows name and mobile (or muted "Not specified"), plus a
 *     small pencil affordance in the top-right when editing is allowed.
 *   • Edit    — two inline text inputs (name, mobile), Save + Cancel buttons,
 *     and inline error text on failure.
 *
 * The component stays uncontrolled in display mode (it just reads the props
 * passed in) and seeds its draft state from the same props whenever it enters
 * edit mode, so prop changes between sessions are picked up correctly. Both
 * fields are optional — saving with empty strings clears the values on the
 * booking (the parent translates '' → null in the update body).
 *
 * Props
 *   • title       — section heading (e.g. "Drop-off person")
 *   • name        — current name string (nullable)
 *   • mobile      — current mobile string (nullable)
 *   • editable    — when false, the pencil is hidden and edit mode is blocked
 *                   (used for terminal-state bookings)
 *   • saving      — parent-level "an update is in flight" flag; while true the
 *                   Save button shows a spinner and inputs are disabled
 *   • accent      — Tailwind class fragments for the colour scheme (blue for
 *                   drop-off, emerald for pickup) — keeps the visual language
 *                   consistent with the booking-form Step 3 tiles
 *   • onSave      — async ({ name, mobile }) => void. Resolves when the parent
 *                   has accepted the update; rejects (or throws) to signal an
 *                   error which is then shown inline. May also return an object
 *                   with { error: string } as a non-throwing failure path.
 */
const InlinePersonEditor = ({ title, name, mobile, editable, saving, accent, onSave }) => {
    const [editing, setEditing] = useState(false);
    const [draftName, setDraftName]     = useState(name ?? '');
    const [draftMobile, setDraftMobile] = useState(mobile ?? '');
    const [error, setError] = useState(null);

    const hasValue = !!(name || mobile);

    const startEdit = () => {
        setDraftName(name ?? '');
        setDraftMobile(mobile ?? '');
        setError(null);
        setEditing(true);
    };

    const cancelEdit = () => {
        setError(null);
        setEditing(false);
    };

    const submit = async () => {
        const trimmedName   = draftName.trim();
        const trimmedMobile = draftMobile.trim();
        // No-op when nothing changed — just exit edit mode quietly.
        if (trimmedName === (name ?? '').trim() && trimmedMobile === (mobile ?? '').trim()) {
            setEditing(false);
            return;
        }
        setError(null);
        try {
            const result = await onSave?.({
                name:   trimmedName === '' ? null : trimmedName,
                mobile: trimmedMobile === '' ? null : trimmedMobile,
            });
            if (result && result.error) {
                setError(result.error);
                return;
            }
            setEditing(false);
        } catch (err) {
            setError(err?.message || 'Failed to save. Please try again.');
        }
    };

    return (
        <div className={`${accent.bg} border ${accent.border} rounded-xl p-3 space-y-2`}>
            <div className="flex items-center justify-between gap-2">
                <h3 className={`text-xs font-semibold ${accent.headerText} uppercase tracking-wide flex items-center gap-1.5`}>
                    <User size={12} className={accent.icon} /> {title}
                </h3>
                {editable && !editing && (
                    <button type="button"
                            onClick={startEdit}
                            title={`Edit ${title.toLowerCase()}`}
                            className={`p-1 rounded ${accent.headerText} ${accent.editHover} transition-colors`}>
                        <Edit2 size={12} />
                    </button>
                )}
            </div>

            {editing ? (
                <div className="space-y-2">
                    <input
                        type="text"
                        value={draftName}
                        onChange={(e) => setDraftName(e.target.value)}
                        placeholder="Name"
                        disabled={saving}
                        className={`w-full px-2.5 py-1.5 text-sm bg-white border border-gray-200 rounded-md focus:ring-1 ${accent.focusRing} focus:outline-none disabled:opacity-60`}
                    />
                    <input
                        type="tel"
                        value={draftMobile}
                        onChange={(e) => setDraftMobile(e.target.value)}
                        placeholder="Mobile"
                        disabled={saving}
                        className={`w-full px-2.5 py-1.5 text-sm bg-white border border-gray-200 rounded-md focus:ring-1 ${accent.focusRing} focus:outline-none disabled:opacity-60`}
                    />
                    {error && (
                        <p className="text-[11px] text-rose-600 flex items-start gap-1">
                            <AlertCircle size={11} className="shrink-0 mt-0.5" />
                            <span>{error}</span>
                        </p>
                    )}
                    <div className="flex items-center gap-1.5 pt-0.5">
                        <button type="button"
                                onClick={submit}
                                disabled={saving}
                                className={`flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1 text-xs font-medium text-white rounded-md ${accent.saveBtn} transition-colors disabled:opacity-50`}>
                            {saving && <Loader2 size={11} className="animate-spin" />}
                            Save
                        </button>
                        <button type="button"
                                onClick={cancelEdit}
                                disabled={saving}
                                className="px-2 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50">
                            Cancel
                        </button>
                    </div>
                </div>
            ) : hasValue ? (
                <div className="space-y-1">
                    {name && (
                        <div className="text-sm text-gray-900 font-medium truncate">{name}</div>
                    )}
                    {mobile && (
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                            <Phone size={12} className={`${accent.phoneIcon} shrink-0`} />
                            <a href={`tel:${mobile}`} className={`${accent.phoneHover} truncate`}>
                                {mobile}
                            </a>
                        </div>
                    )}
                </div>
            ) : (
                <p className={`text-sm ${accent.emptyText} italic`}>Same as customer</p>
            )}
        </div>
    );
};

/**
 * InlineDateTimeEditor
 *
 * Replaces the static "time · date" line in BookingDetailModal with an
 * inline edit affordance. Click the pencil → date input + time select
 * (the same TIME_SLOTS used on the booking form). Save sends just the
 * two changed fields through the parent's onSave; the rest of the booking
 * body is rebuilt from current state in BookingsPage via
 * buildUpdateBodyFromBooking, which keeps the PUT idempotent.
 *
 * The component stays uncontrolled in display mode (reads props directly)
 * and seeds its draft from props whenever it enters edit mode, so prop
 * changes between sessions are picked up correctly.
 *
 * Props
 *   • scheduledDate — current YYYY-MM-DD string (or null)
 *   • scheduledTime — current HH:MM:SS string (or null)
 *   • editable      — when false, the pencil is hidden
 *   • saving        — parent-level "an update is in flight" flag
 *   • onSave        — async ({ scheduledDate, scheduledTime }) =>
 *                     null | { error: string }. Returns { error } on a
 *                     non-throwing failure so we can show inline feedback
 *                     without the user losing their draft.
 */
const InlineDateTimeEditor = ({ scheduledDate, scheduledTime, editable, saving, onSave }) => {
    const [editing, setEditing] = useState(false);
    const [draftDate, setDraftDate] = useState(scheduledDate ?? '');
    const [draftTime, setDraftTime] = useState((scheduledTime ?? '').substring(0, 5));
    const [error, setError] = useState(null);

    const startEdit = () => {
        setDraftDate(scheduledDate ?? '');
        setDraftTime((scheduledTime ?? '').substring(0, 5));
        setError(null);
        setEditing(true);
    };

    const cancelEdit = () => {
        setError(null);
        setEditing(false);
    };

    const submit = async () => {
        if (!draftDate) {
            setError('Date is required.');
            return;
        }
        const currentTime = (scheduledTime ?? '').substring(0, 5);
        // No-op when nothing changed.
        if (draftDate === (scheduledDate ?? '') && draftTime === currentTime) {
            setEditing(false);
            return;
        }
        setError(null);
        try {
            const result = await onSave?.({
                scheduledDate: draftDate,
                scheduledTime: draftTime === '' ? null : draftTime,
            });
            if (result && result.error) {
                setError(result.error);
                return;
            }
            setEditing(false);
        } catch (err) {
            setError(err?.message || 'Failed to save. Please try again.');
        }
    };

    if (!editing) {
        return (
            <div className="flex items-center gap-2 text-sm text-gray-500">
                <Clock size={14} className="text-gray-400" />
                <span>
                    {formatTime(scheduledTime)} · {formatShortDate(scheduledDate)}
                </span>
                {editable && (
                    <button type="button"
                            onClick={startEdit}
                            title="Edit date & time"
                            className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors">
                        <Edit2 size={12} />
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <Clock size={12} /> Date & time
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
                <input
                    type="date"
                    value={draftDate}
                    onChange={(e) => setDraftDate(e.target.value)}
                    disabled={saving}
                    className="flex-1 px-2.5 py-1.5 text-sm bg-white border border-gray-200 rounded-md focus:ring-1 focus:ring-violet-500 focus:border-violet-500 focus:outline-none disabled:opacity-60"
                />
                <select
                    value={draftTime}
                    onChange={(e) => setDraftTime(e.target.value)}
                    disabled={saving}
                    className="flex-1 px-2.5 py-1.5 text-sm bg-white border border-gray-200 rounded-md focus:ring-1 focus:ring-violet-500 focus:border-violet-500 focus:outline-none disabled:opacity-60"
                >
                    <option value="">No time</option>
                    {TIME_SLOTS.map(t => (
                        <option key={t} value={t}>{t}</option>
                    ))}
                </select>
            </div>
            {error && (
                <p className="text-[11px] text-rose-600 flex items-start gap-1">
                    <AlertCircle size={11} className="shrink-0 mt-0.5" />
                    <span>{error}</span>
                </p>
            )}
            <div className="flex items-center justify-end gap-1.5 pt-0.5">
                <button type="button"
                        onClick={cancelEdit}
                        disabled={saving}
                        className="px-3 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50">
                    Cancel
                </button>
                <button type="button"
                        onClick={submit}
                        disabled={saving}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1 text-xs font-medium text-white rounded-md bg-violet-600 hover:bg-violet-700 transition-colors disabled:opacity-50">
                    {saving && <Loader2 size={11} className="animate-spin" />}
                    Save
                </button>
            </div>
        </div>
    );
};

/**
 * InlineServicesEditor
 *
 * Inline edit for the Services section of BookingDetailModal. In display
 * mode it renders the existing service-type cards (one per entry) with a
 * small pencil next to the section heading. Edit mode swaps them for a
 * row-per-service form: type dropdown (from bookingServiceCategories), duration in
 * minutes, and a free-text description. Rows can be added or removed.
 *
 * Service-profile bookings are excluded — those are driven by the
 * ServiceProfile workflow on the booking form and the PUT endpoint stores
 * an empty serviceTypes array for them (see buildUpdateBodyFromBooking).
 * For those bookings we render display mode only with no pencil.
 *
 * estimatedDurationMinutes is recomputed from the row durations on save
 * (mirrors the create flow's FE-computed sum noted in bookingApi.js).
 *
 * Props
 *   • booking        — full BookingResponse (used for display + isProfile)
 *   • bookingServiceCategories  — [{ value, label, description }] from /bookings/meta/service-categories
 *   • editable       — when false, the pencil is hidden
 *   • saving         — parent-level "an update is in flight" flag
 *   • onSave         — async ({ serviceTypes, estimatedDurationMinutes }) =>
 *                      null | { error: string }
 */
const InlineServicesEditor = ({ booking, bookingServiceCategories, editable, saving, onSave }) => {
    const isProfile = !!booking.serviceProfileName;
    const types = getBookingServiceTypes(booking);
    const heading = isProfile ? 'Service Profile' : (types.length > 1 ? 'Services' : 'Service');

    const [editing, setEditing] = useState(false);
    const [draftRows, setDraftRows] = useState([]);
    const [error, setError] = useState(null);

    const seedDraftFromBooking = () => {
        if (types.length === 0) {
            return [{ type: '', durationHours: '', description: '' }];
        }
        return types.map(t => ({
            type: t.type ?? '',
            // Convert stored minutes to hours for the editor input. Trim to 2dp
            // to avoid noisy values like 0.75000000001; quarter-hour granularity
            // matches the input step.
            durationHours: t.durationMinutes != null && t.durationMinutes !== ''
                ? String(Math.round((t.durationMinutes / 60) * 100) / 100)
                : '',
            description: t.description ?? '',
        }));
    };

    const startEdit = () => {
        setDraftRows(seedDraftFromBooking());
        setError(null);
        setEditing(true);
    };

    const cancelEdit = () => {
        setError(null);
        setEditing(false);
    };

    const updateRow = (idx, field, value) => {
        setDraftRows(rows => rows.map((r, i) => i === idx ? { ...r, [field]: value } : r));
    };

    const addRow = () => {
        setDraftRows(rows => [...rows, { type: '', durationHours: '', description: '' }]);
    };

    const removeRow = (idx) => {
        setDraftRows(rows => rows.filter((_, i) => i !== idx));
    };

    const submit = async () => {
        // Convert the hours-valued draft back into the minutes shape the backend
        // expects. Empty inputs stay null. Empty list is allowed (matches what
        // the backend stores for profile-backed bookings, though we only reach
        // this branch for ad-hoc bookings).
        const cleaned = draftRows
            .map(r => {
                const hours = r.durationHours === '' ? null : Number(r.durationHours);
                const minutes = hours == null ? null : Math.round(hours * 60);
                return {
                    type: (r.type ?? '').trim(),
                    durationMinutes: minutes,
                    description: (r.description ?? '').trim() === '' ? null : r.description.trim(),
                };
            })
            .filter(r => r.type !== '');

        if (cleaned.length === 0) {
            setError('At least one service is required.');
            return;
        }

        for (const r of cleaned) {
            if (r.durationMinutes !== null) {
                if (!Number.isFinite(r.durationMinutes) || r.durationMinutes < 1) {
                    setError('Duration must be a positive number of hours.');
                    return;
                }
                if (r.durationMinutes > 1440) {
                    setError('Duration cannot exceed 24 hours.');
                    return;
                }
            }
        }

        const totalDuration = cleaned.reduce((sum, r) => sum + (Number.isFinite(r.durationMinutes) ? r.durationMinutes : 0), 0);

        setError(null);
        try {
            const result = await onSave?.({
                serviceTypes: cleaned,
                estimatedDurationMinutes: totalDuration > 0 ? totalDuration : null,
            });
            if (result && result.error) {
                setError(result.error);
                return;
            }
            setEditing(false);
        } catch (err) {
            setError(err?.message || 'Failed to save. Please try again.');
        }
    };

    // Profile-backed bookings: render the existing display-only view.
    // Editing the underlying service profile is a separate flow, so no
    // pencil here.
    if (isProfile || !editing) {
        return (
            <div>
                <div className="flex items-baseline justify-between mb-2">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
                        {heading}
                        {isProfile && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 text-[10px] font-semibold uppercase tracking-wider">
                                Profile
                            </span>
                        )}
                        {!isProfile && editable && (
                            <button type="button"
                                    onClick={startEdit}
                                    title="Edit services"
                                    className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors">
                                <Edit2 size={11} />
                            </button>
                        )}
                    </h3>
                    {types.length > 1 && (
                        <span className="text-[10px] text-gray-400">
                            {types.length} items
                        </span>
                    )}
                </div>

                {types.length === 0 ? (
                    <p className="text-gray-400 italic text-sm">Not specified</p>
                ) : (
                    <div className="space-y-2">
                        {types.map((entry, idx) => {
                            const duration = formatDurationShort(entry.durationMinutes);
                            return (
                                <div
                                    key={`${entry.type}-${idx}`}
                                    className="rounded-lg border border-gray-200 bg-white px-3 py-2"
                                >
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold tracking-wide ${
                                            isProfile
                                                ? 'bg-indigo-50 text-indigo-700'
                                                : 'bg-violet-50 text-violet-700'
                                        }`}>
                                            {formatServiceTypeLabel(entry.type)}
                                        </span>
                                        {duration && (
                                            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                                <Clock size={11} />
                                                {duration}
                                            </span>
                                        )}
                                    </div>
                                    {entry.description && (
                                        <p className="text-sm text-gray-700 mt-1.5 leading-snug whitespace-pre-wrap">
                                            {entry.description}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {booking.estimatedDurationMinutes && (
                    <p className="text-sm text-gray-500 flex items-center gap-1 mt-2">
                        <Clock size={13} />
                        Total estimated: {formatDurationShort(booking.estimatedDurationMinutes) ?? `${booking.estimatedDurationMinutes} min`}
                    </p>
                )}
            </div>
        );
    }

    // ── Edit mode ──
    return (
        <div>
            <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
                    Services
                </h3>
                <button type="button"
                        onClick={addRow}
                        disabled={saving}
                        className="text-[11px] font-medium text-violet-700 hover:text-violet-900 disabled:opacity-50 inline-flex items-center gap-1">
                    <Plus size={11} /> Add service
                </button>
            </div>
            <div className="space-y-2">
                {draftRows.map((row, idx) => (
                    <div key={idx} className="rounded-lg border border-gray-200 bg-gray-50 p-2.5 space-y-2">
                        <div className="flex items-center gap-1.5">
                            <select
                                value={row.type}
                                onChange={(e) => updateRow(idx, 'type', e.target.value)}
                                disabled={saving}
                                className="flex-1 px-2.5 py-1.5 text-sm bg-white border border-gray-200 rounded-md focus:ring-1 focus:ring-violet-500 focus:border-violet-500 focus:outline-none disabled:opacity-60"
                            >
                                <option value="">Select service…</option>
                                {bookingServiceCategories.map(ct => (
                                    <option key={ct.value} value={ct.value}>{ct.label}</option>
                                ))}
                            </select>
                            <div className="relative">
                                <input
                                    type="number"
                                    min="0"
                                    step="0.25"
                                    value={row.durationHours}
                                    onChange={(e) => updateRow(idx, 'durationHours', e.target.value)}
                                    disabled={saving}
                                    placeholder="hrs"
                                    className="w-24 pl-2 pr-8 py-1.5 text-sm bg-white border border-gray-200 rounded-md focus:ring-1 focus:ring-violet-500 focus:border-violet-500 focus:outline-none disabled:opacity-60"
                                />
                                <span className="pointer-events-none absolute right-7 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">hrs</span>
                            </div>
                            {draftRows.length > 1 && (
                                <button type="button"
                                        onClick={() => removeRow(idx)}
                                        disabled={saving}
                                        title="Remove this service"
                                        className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors disabled:opacity-50">
                                    <Trash2 size={13} />
                                </button>
                            )}
                        </div>
                        <textarea
                            value={row.description}
                            onChange={(e) => updateRow(idx, 'description', e.target.value)}
                            disabled={saving}
                            placeholder="Notes (optional)"
                            rows={2}
                            className="w-full px-2.5 py-1.5 text-sm bg-white border border-gray-200 rounded-md focus:ring-1 focus:ring-violet-500 focus:border-violet-500 focus:outline-none disabled:opacity-60 resize-none"
                        />
                    </div>
                ))}
            </div>
            {error && (
                <p className="text-[11px] text-rose-600 flex items-start gap-1 mt-2">
                    <AlertCircle size={11} className="shrink-0 mt-0.5" />
                    <span>{error}</span>
                </p>
            )}
            <div className="flex items-center justify-end gap-1.5 mt-2">
                <button type="button"
                        onClick={cancelEdit}
                        disabled={saving}
                        className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50">
                    Cancel
                </button>
                <button type="button"
                        onClick={submit}
                        disabled={saving}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-md bg-violet-600 hover:bg-violet-700 transition-colors disabled:opacity-50">
                    {saving && <Loader2 size={11} className="animate-spin" />}
                    Save services
                </button>
            </div>
        </div>
    );
};

const BookingDetailModal = ({ booking, bookingServiceCategories, loanCarIncidents, loanCarIncidentsLoading, onClose, onEdit, onCancel, onStatusChange, onSavePersons, onSaveBookingFields, onViewJob, loading }) => {
    // Desktop-only Escape to close.
    useEscapeToClose(true, onClose);

    // Once a booking reaches VEHICLE_RECEIVED, the workshop has taken physical
    // possession of the vehicle and an associated job has been created — at
    // that point booking-level changes (status updates, edits, cancellation)
    // must be made on the job, not the booking, to keep the two in sync.
    // CANCELLED and COMPLETED are terminal so they're hidden for the same reason.
    const canModifyBooking =
        booking.status !== 'CANCELLED'
        && booking.status !== 'COMPLETED'
        && booking.status !== 'VEHICLE_RECEIVED';

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col shadow-2xl">

                {/* Header */}
                <div className="flex items-center justify-between p-5 pb-4 border-b border-gray-100 shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">Booking Details</h2>
                        {booking.bookingCode && (
                            <p className="text-sm text-gray-500 font-mono">{booking.bookingCode}</p>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        {/*
                         * View Job — promotes the booking → job navigation to
                         * the header so it's visible above the fold regardless
                         * of how far the user has scrolled in the body. Only
                         * rendered when the booking has an auto-created job
                         * (jobUuid populated). Compact button styling matches
                         * the status pill next to it.
                         */}
                        {booking.jobUuid && onViewJob && (
                            <button
                                onClick={onViewJob}
                                className="flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border border-violet-200 text-violet-700 hover:bg-violet-50 transition-colors"
                                title="View the job created for this booking"
                            >
                                <Wrench size={13} /> View Job
                            </button>
                        )}
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(booking.status)}`}>
                            {booking.status.replace(/_/g, ' ')}
                        </span>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Body — two columns when handover panel is visible, single column otherwise */}
                <div className="overflow-y-auto flex-1 p-5">
                    <div className={`grid gap-5 ${booking.status === 'VEHICLE_RECEIVED' && booking.loanCarSummary ? 'grid-cols-2' : 'grid-cols-1'}`}>

                        {/* ── LEFT COLUMN ── */}
                        <div className="space-y-4">

                            {/* Date / Time — inline editable when the booking is still modifiable */}
                            <InlineDateTimeEditor
                                scheduledDate={booking.scheduledDate}
                                scheduledTime={booking.scheduledTime}
                                editable={canModifyBooking}
                                saving={loading}
                                onSave={({ scheduledDate, scheduledTime }) =>
                                    onSaveBookingFields?.({ scheduledDate, scheduledTime })
                                }
                            />

                            {/* Services — inline editable for ad-hoc bookings (profile-backed are read-only here) */}
                            <InlineServicesEditor
                                booking={booking}
                                bookingServiceCategories={bookingServiceCategories}
                                editable={canModifyBooking}
                                saving={loading}
                                onSave={({ serviceTypes, estimatedDurationMinutes }) =>
                                    onSaveBookingFields?.({ serviceTypes, estimatedDurationMinutes })
                                }
                            />

                            {/* Customer */}
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-2">
                                <h3 className="text-xs font-semibold text-blue-700 uppercase tracking-wide flex items-center gap-1.5">
                                    <User size={13} className="text-blue-600" /> Customer
                                </h3>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-gray-900">{booking.customerName}</span>
                                    {booking.customerType === 'BUSINESS' && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold uppercase tracking-wider">
                                            <Building2 size={10} />
                                            Company
                                        </span>
                                    )}
                                </div>
                                {booking.customerPhone && (
                                    <div className="flex items-center gap-2 text-sm text-gray-700">
                                        <Phone size={13} className="text-blue-500" />
                                        <a href={`tel:${booking.customerPhone}`} className="hover:text-blue-700">{booking.customerPhone}</a>
                                    </div>
                                )}
                                {booking.customerEmail && (
                                    <div className="flex items-center gap-2 text-sm text-gray-700">
                                        <Mail size={13} className="text-blue-500" />
                                        <a href={`mailto:${booking.customerEmail}`} className="hover:text-blue-700">{booking.customerEmail}</a>
                                    </div>
                                )}
                            </div>

                            {/* Drop-off & Pickup persons — captured at booking time, both optional.
                                Each side renders as its own coloured tile (blue for drop-off,
                                emerald for pickup) matching the colour scheme used on Step 3 of
                                the booking form, so the two screens read in the same visual
                                language. Each tile renders independently:
                                  - has name and/or phone → show what we have, with a tel: link on phone
                                  - completely empty       → show muted "Not specified"
                                The card is always shown so staff can see at a glance that no
                                drop-off / pickup person was nominated. */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {/* Drop-off (handover) — blue. Inline-editable when the booking
                                    is still modifiable (not yet received / cancelled / completed). */}
                                <InlinePersonEditor
                                    title="Drop-off person"
                                    name={booking.handoverPersonName}
                                    mobile={booking.handoverPersonMobile}
                                    editable={canModifyBooking}
                                    saving={loading}
                                    accent={{
                                        bg:          'bg-blue-50',
                                        border:      'border-blue-200',
                                        headerText:  'text-blue-700',
                                        icon:        'text-blue-600',
                                        phoneIcon:   'text-blue-500',
                                        phoneHover:  'hover:text-blue-700',
                                        emptyText:   'text-blue-400',
                                        editHover:   'hover:bg-blue-100 hover:text-blue-700',
                                        focusRing:   'focus:ring-blue-500 focus:border-blue-500',
                                        saveBtn:     'bg-blue-600 hover:bg-blue-700',
                                    }}
                                    onSave={({ name, mobile }) =>
                                        onSavePersons?.({
                                            handoverPersonName:   name,
                                            handoverPersonMobile: mobile,
                                        })
                                    }
                                />

                                {/* Pickup — emerald. */}
                                <InlinePersonEditor
                                    title="Pickup person"
                                    name={booking.pickupPersonName}
                                    mobile={booking.pickupPersonMobile}
                                    editable={canModifyBooking}
                                    saving={loading}
                                    accent={{
                                        bg:          'bg-emerald-50',
                                        border:      'border-emerald-200',
                                        headerText:  'text-emerald-700',
                                        icon:        'text-emerald-600',
                                        phoneIcon:   'text-emerald-500',
                                        phoneHover:  'hover:text-emerald-700',
                                        emptyText:   'text-emerald-400',
                                        editHover:   'hover:bg-emerald-100 hover:text-emerald-700',
                                        focusRing:   'focus:ring-emerald-500 focus:border-emerald-500',
                                        saveBtn:     'bg-emerald-600 hover:bg-emerald-700',
                                    }}
                                    onSave={({ name, mobile }) =>
                                        onSavePersons?.({
                                            pickupPersonName:   name,
                                            pickupPersonMobile: mobile,
                                        })
                                    }
                                />
                            </div>

                            {/* Vehicle */}
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1">
                                <h3 className="text-xs font-semibold text-amber-700 uppercase tracking-wide flex items-center gap-1.5">
                                    <Car size={13} className="text-amber-600" /> Vehicle
                                </h3>
                                <div className="flex items-center gap-2 flex-wrap pt-1">
                                    <span className="font-semibold text-gray-900">{booking.vehicleDescription || booking.vehicleRego}</span>
                                    <TransmissionBadge value={booking.vehicleTransmission} variant="pill" />
                                </div>
                                {booking.vehicleRego && (
                                    <p className="text-sm text-amber-900/70 font-mono">Rego: {booking.vehicleRego}</p>
                                )}
                            </div>

                            {/* Loan Car — assign / remove allocation */}
                            <div className="bg-violet-50 border border-violet-200 rounded-xl p-3">
                                <LoanCarAllocationPanel booking={booking} />
                            </div>

                            {/* Description / Notes */}
                            {booking.description && (
                                <div>
                                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Notes</h3>
                                    <p className="text-sm text-gray-700">{booking.description}</p>
                                </div>
                            )}

                            {/* Status change */}
                            {canModifyBooking && (
                                <div>
                                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Update Status</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {BOOKING_STATUSES.filter(s => s !== booking.status).map(status => (
                                            <button
                                                key={status}
                                                onClick={() => onStatusChange(status)}
                                                disabled={loading}
                                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${getStatusBadgeColor(status)} hover:opacity-80 disabled:opacity-50`}
                                            >
                                                {status === 'VEHICLE_RECEIVED' ? '🚗 Vehicle Received' : status.replace(/_/g, ' ')}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ── RIGHT COLUMN — Loan Car Handover (VEHICLE_RECEIVED + loan car only) ── */}
                        {booking.status === 'VEHICLE_RECEIVED' && booking.loanCarSummary && (
                            <div className="space-y-3">
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
                                    <Car size={13} />
                                    Loan Car Handover
                                </h3>

                                {/* ── Allocation summary card ── */}
                                {booking.loanCarSummary && (
                                    <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-3 space-y-2">
                                        {/* Rego */}
                                        <div className="flex items-center gap-2">
                                            <Car size={14} className="text-blue-500 shrink-0" />
                                            <span className="text-sm font-bold text-blue-800 font-mono tracking-wide">
                                            {booking.loanCarSummary.rego}
                                        </span>
                                            <span className="text-xs text-blue-400 font-normal">loan car</span>
                                        </div>

                                        {/* Expected return */}
                                        {booking.loanCarSummary.allocationEnd && (
                                            <div className="flex items-start gap-2 text-sm">
                                                <CalendarClock size={13} className="text-blue-400 shrink-0 mt-0.5" />
                                                <span className="text-blue-700 leading-snug">
                                                Expected return:{' '}
                                                    <span className="font-semibold">
                                                    {formatDateTime(booking.loanCarSummary.allocationEnd)}
                                                </span>
                                            </span>
                                            </div>
                                        )}

                                        {/* Allocation notes */}
                                        {booking.loanCarSummary.notes && (
                                            <p className="text-xs text-blue-600 bg-white rounded-lg px-3 py-2 border border-blue-100 leading-relaxed">
                                                {booking.loanCarSummary.notes}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* ── Handover incidents ── */}
                                {loanCarIncidentsLoading ? (
                                    <div className="flex items-center gap-2 text-sm text-gray-400 p-4">
                                        <Loader2 size={15} className="animate-spin" />
                                        Loading handover records…
                                    </div>
                                ) : loanCarIncidents.length === 0 ? (
                                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-center gap-2 text-sm text-amber-600">
                                        <Info size={15} />
                                        Loan car attached — handover not yet recorded
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {loanCarIncidents.map((incident) => {
                                            // Pass the OUT odometer to the IN card so km delta can be shown
                                            const outOdometer = incident.type === 'IN'
                                                ? loanCarIncidents.find(i => i.type === 'OUT')?.odometerReading
                                                : undefined;
                                            return (
                                                <IncidentCard
                                                    key={incident.incidentId}
                                                    incident={incident}
                                                    outOdometer={outOdometer}
                                                />
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer actions */}
                <div className="p-5 pt-0 shrink-0 flex gap-3 border-t border-gray-100 mt-2">
                    {canModifyBooking && (
                        <>
                            <button onClick={onEdit}
                                    className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
                                <Edit2 size={15} /> Edit
                            </button>
                            <button onClick={onCancel}
                                    className="flex-1 py-2.5 border border-red-200 text-red-600 rounded-xl font-medium hover:bg-red-50 transition-colors flex items-center justify-center gap-2">
                                <Trash2 size={15} /> Cancel
                            </button>
                        </>
                    )}
                    <button onClick={onClose}
                            className={`${!canModifyBooking ? 'flex-1' : ''} py-2.5 px-5 bg-gray-100 rounded-xl text-gray-700 font-medium hover:bg-gray-200 transition-colors`}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==================== Assign Staff Picker ====================

/**
 * Inline AssignStaffPicker — a compact modal for picking a staff member to
 * assign to a job, used by the booking-launched JobDetailModal flow on
 * BookingsPage. JobsPage has its own dedicated AssignStaffModal; this one
 * exists so staff don't have to leave the calendar to assign work.
 *
 * Self-contained: fetches GET /staff on mount via axiosInstance and renders
 * a click-to-select list. No staff slice dependency keeps the change local
 * and avoids assumptions about the project's staff state shape. If the
 * endpoint path or response shape differs, update fetchStaff() below.
 *
 * Sits at z-[60] so it stacks above JobDetailModal (z-50). Escape closes
 * the picker only — JobDetailModal underneath stays open.
 */
const AssignStaffPicker = ({ jobUuid, loading, onClose, onSelect }) => {
    useEscapeToClose(true, onClose);

    const [staff, setStaff]       = useState([]);
    const [fetching, setFetching] = useState(true);
    const [error, setError]       = useState(null);
    const [search, setSearch]     = useState('');

    useEffect(() => {
        let cancelled = false;
        const fetchStaff = async () => {
            try {
                const response = await axiosInstance.get('/staff');
                if (cancelled) return;
                // Tolerate either a bare array or a Page<Staff>
                const list = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.content ?? []);
                setStaff(list);
            } catch (err) {
                if (cancelled) return;
                setError(err.response?.data?.message || 'Failed to load staff');
            } finally {
                if (!cancelled) setFetching(false);
            }
        };
        fetchStaff();
        return () => { cancelled = true; };
    }, []);

    // Tolerate fullName | name | firstName/lastName for the display label —
    // again, no assumption about the staff record shape.
    const displayName = (s) =>
        s.fullName
        ?? s.name
        ?? [s.firstName, s.lastName].filter(Boolean).join(' ')
        ?? s.email
        ?? s.uuid;

    const filtered = search.trim() === ''
        ? staff
        : staff.filter(s => displayName(s).toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">Assign Staff</h2>
                        <p className="text-xs text-gray-500 mt-0.5">Select a staff member for this job</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Search */}
                <div className="px-5 pt-4 pb-2 shrink-0">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name…"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-violet-400"
                        autoFocus
                    />
                </div>

                {/* Body */}
                <div className="overflow-y-auto flex-1 px-5 py-3">
                    {fetching && (
                        <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-8">
                            <Loader2 size={15} className="animate-spin" /> Loading staff…
                        </div>
                    )}
                    {error && !fetching && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                            <AlertCircle size={15} className="shrink-0 mt-0.5" />
                            <span>{error}</span>
                        </div>
                    )}
                    {!fetching && !error && filtered.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-8">
                            {search.trim() === '' ? 'No staff found' : 'No matches'}
                        </p>
                    )}
                    {!fetching && !error && filtered.length > 0 && (
                        <ul className="divide-y divide-gray-100">
                            {filtered.map((s) => (
                                <li key={s.uuid}>
                                    <button
                                        onClick={() => onSelect(s.uuid)}
                                        disabled={loading}
                                        className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-violet-50 disabled:opacity-50 rounded-lg transition-colors"
                                    >
                                        <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center shrink-0">
                                            <User size={14} className="text-violet-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                {displayName(s)}
                                            </p>
                                            {s.email && (
                                                <p className="text-xs text-gray-500 truncate">{s.email}</p>
                                            )}
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Footer */}
                <div className="p-5 pt-3 border-t border-gray-100 shrink-0">
                    <button
                        onClick={onClose}
                        className="w-full py-2.5 bg-gray-100 rounded-xl text-gray-700 font-medium hover:bg-gray-200 transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==================== Cancel Confirm Modal ====================

const CancelConfirmModal = ({ booking, onClose, onConfirm, loading }) => {
    // Desktop-only Escape to close.
    useEscapeToClose(true, onClose);

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-sm w-full p-6">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle size={24} className="text-red-600" />
                </div>
                <h2 className="text-lg font-bold text-gray-900 text-center mb-2">Cancel Booking?</h2>
                <p className="text-gray-500 text-center mb-4">
                    Are you sure you want to cancel the booking for{' '}
                    <span className="font-medium text-gray-900">{booking.customerName}</span> on{' '}
                    <span className="font-medium text-gray-900">{formatShortDate(booking.scheduledDate)}</span>?
                </p>
                <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg mb-6">
                    The booking will be marked as cancelled and won't appear in the calendar, but the record will be kept for historical purposes.
                </p>
                <div className="flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                    >
                        Keep Booking
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        className="flex-1 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {loading && <Loader2 size={18} className="animate-spin" />}
                        Cancel Booking
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BookingsPage;